/*
  WATCHERS V2 — PERSISTENT STOREFRONT NAVIGATION
  BUILD: 2026-09-29-N45-MINIMAL-PRODUCT-404-SHOP-ALL

  Goal:
  - Keep announcement + header mounted during normal same-origin storefront navigation.
  - Keep the current page visible while the next page is fetched.
  - Replace only #MainContent after the next page is ready.
  - Avoid the full-document white flash between Shopify pages.

  Safety:
  - Disabled in Shopify theme editor/design mode.
  - Does not intercept checkout, customer account, admin, app proxy, downloads,
    external links, modified clicks, or the cart drawer trigger.
  - N39 intercepts ONLY the storefront HEADER GET /search form so search can use
    the same persistent #MainContent navigation path without a full-page white flash.
  - Shopify/Dawn facet operations remain on the facet AJAX path.
  - Falls back to normal navigation if a fetch/parse/swap fails.
*/

(() => {
  'use strict';

  if (window.__WATCHERS_PERSISTENT_NAV__) return;
  window.__WATCHERS_PERSISTENT_NAV__ = true;

  if (window.Shopify && Shopify.designMode) return;

  /*
    N35 — RESTORE THE PROVEN PAGINATION/BACK SCROLL CONTRACT.
    WATCHERS owns scroll restoration while persistent navigation is active.
    This prevents mobile Safari/Chrome from restoring an old Y-position against
    the still-mounted previous DOM before #MainContent has been swapped.
  */
  try { history.scrollRestoration = 'manual'; } catch (_) {}

  const MAIN_SELECTOR = '#MainContent';
  const NAV_CLASS = 'watchers-nav-loading';

  const EXCLUDED_PREFIXES = [
    '/checkout',
    '/account',
    '/admin',
    '/apps',
    '/challenge',
    '/password'
  ];

  let controller = null;
  let navigating = false;
  let lastRequestedUrl = '';

  /*
    N29 — GLOBAL PHONE/BROWSER BACK GUARD FOR MOBILE FILTER DRAWER

    This belongs in the persistent router, not inside one collection section.
    When Back is pressed while the collection filter drawer is open, the browser
    has already moved one history entry backward before popstate fires. N28 closes
    only the drawer, immediately returns to the same history entry, and consumes
    the forward popstate so #MainContent underneath never navigates.
  */
  /*
    N37 — GLOBAL PHONE/BROWSER BACK GUARD FOR CART DRAWER

    CART has first Back priority, before FILTER and before page navigation.

    Same proven strategy as FILTER:
    - first Back while CART is open: browser has already moved backward
    - close only CART
    - restore the exact underlying-page scroll
    - history.forward() back to the same page entry
    - consume that forward popstate
    - next Back behaves normally
  */
  let restoringAfterCartBack = false;
  let cartOpenScrollSnapshot = null;

  function captureCartOpenScroll() {
    cartOpenScrollSnapshot = {
      x: window.scrollX,
      y: window.scrollY,
      previousScrollRestoration:
        history.scrollRestoration || 'manual'
    };

    try {
      history.scrollRestoration = 'manual';
    } catch (_) {}
  }

  function restoreCartOpenScroll() {
    if (!cartOpenScrollSnapshot) return;

    window.scrollTo({
      left: Number(cartOpenScrollSnapshot.x) || 0,
      top: Number(cartOpenScrollSnapshot.y) || 0,
      behavior: 'auto'
    });
  }

  function releaseCartScrollLock() {
    /*
      WATCHERS persistent navigation continues to own scroll restoration.
      Keep it manual after the transient cart Back round-trip.
    */
    cartOpenScrollSnapshot = null;

    try {
      history.scrollRestoration = 'manual';
    } catch (_) {}
  }

  function cartDrawerIsOpen() {
    const drawer =
      document.querySelector('cart-drawer');

    return Boolean(
      drawer &&
      drawer.classList.contains('active')
    );
  }

  function closeCartDrawerOnly() {
    const drawer =
      document.querySelector('cart-drawer');

    if (!drawer) return;

    if (typeof drawer.close === 'function') {
      drawer.close();
      return;
    }

    drawer.classList.remove(
      'active',
      'animate'
    );
  }

  /*
    Capture the exact page position before a normal header-cart open.
  */
  document.addEventListener(
    'click',
    (event) => {
      const opener =
        event.target.closest?.(
          '#cart-icon-bubble'
        );

      if (!opener) return;

      captureCartOpenScroll();
    },
    true
  );

  /*
    Adds from PDP / collection / Just Dropped can open the cart without the
    customer pressing the header icon. CD24 dispatches this after rendering,
    so capture the underlying page position here too.
  */
  document.addEventListener(
    'watchers:cart:rendered',
    () => {
      if (!cartOpenScrollSnapshot) {
        captureCartOpenScroll();
      }
    }
  );


  let restoringAfterFilterBack = false;
  let filterOpenScrollSnapshot = null;

  function captureFilterOpenScroll() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    filterOpenScrollSnapshot = {
      x: window.scrollX,
      y: window.scrollY,
      previousScrollRestoration: history.scrollRestoration || 'auto'
    };

    try { history.scrollRestoration = 'manual'; } catch (_) {}
  }

  function restoreFilterOpenScroll() {
    if (!filterOpenScrollSnapshot) return;

    window.scrollTo({
      left: Number(filterOpenScrollSnapshot.x) || 0,
      top: Number(filterOpenScrollSnapshot.y) || 0,
      behavior: 'auto'
    });
  }

  function releaseFilterScrollLock() {
    if (!filterOpenScrollSnapshot) return;

    try {
      history.scrollRestoration =
        filterOpenScrollSnapshot.previousScrollRestoration || 'auto';
    } catch (_) {}

    filterOpenScrollSnapshot = null;
  }

  document.addEventListener(
    'click',
    (event) => {
      const opener = event.target.closest?.(
        '[data-watchers-open-filter], #main-collection-filters .mobile-facets__open'
      );
      if (!opener) return;
      captureFilterOpenScroll();
    },
    true
  );

  document.addEventListener(
    'watchers:filter:closed',
    () => {
      if (restoringAfterFilterBack) return;
      releaseFilterScrollLock();
    }
  );

  function mobileFilterDisclosure() {
    return document.querySelector(
      '#main-collection-filters details.mobile-facets__disclosure, ' +
      '#main-collection-filters .mobile-facets__wrapper details'
    );
  }

  function mobileFilterIsOpen() {
    if (!window.matchMedia('(max-width: 749px)').matches) return false;

    const disclosure = mobileFilterDisclosure();
    if (disclosure?.hasAttribute('open')) return true;

    return Boolean(
      document.querySelector('#main-collection-filters .menu-opening .mobile-facets__inner') ||
      document.querySelector('#main-collection-filters .mobile-facets__wrapper.menu-opening')
    );
  }

  function closeMobileFilterOnly() {
    if (typeof window.__WATCHERS_CLOSE_FILTER_DRAWER__ === 'function') {
      window.__WATCHERS_CLOSE_FILTER_DRAWER__();
      return;
    }

    const disclosure = mobileFilterDisclosure();
    if (!disclosure) return;

    const nativeClose = disclosure.querySelector(
      '.mobile-facets__close, .mobile-facets__close-button'
    );

    if (nativeClose instanceof HTMLElement) {
      nativeClose.click();
      return;
    }

    disclosure.open = false;
    disclosure.removeAttribute('open');
    disclosure.classList.remove('menu-opening');

    disclosure
      .querySelectorAll('.menu-opening')
      .forEach((node) => node.classList.remove('menu-opening'));

    const summary = disclosure.querySelector(':scope > summary');
    summary?.setAttribute('aria-expanded', 'false');
  }

  /*
    N18.5 — PERSISTENT NAVIGATION PRELOAD CACHE

    Only collection/home destinations that the customer is likely to press are
    warmed in the background. Successful entries remain reusable for several
    minutes so forward/back navigation can reuse the same HTML instead of refetching.
  */

  const navigationPreloadCache = new Map();
  const NAVIGATION_PRELOAD_TTL = 2 * 60 * 1000;
  const NAVIGATION_PRELOAD_MAX = 48;


  /*
    N24 — COLLECTION FACET RUNTIME WARMUP

    Persistent navigation can make collection HTML appear before Dawn's
    facets.js has finished loading. The WATCHERS drawer runtime can then update
    selected-filter chips even though the real FacetFiltersForm listener is not
    ready yet, making the first filter look selected without updating products.

    Warm facets.js from prefetched collection HTML so the custom element is
    already defined before the collection becomes interactive.
  */
  let facetRuntimeWarmPromise = null;

  function extractFacetRuntimeSrc(html) {
    if (!html || typeof html !== 'string') return '';

    const match = html.match(
      /<script[^>]+src=["']([^"']*\/facets\.js(?:\?[^"']*)?)["'][^>]*>/i
    );

    if (!match || !match[1]) return '';

    const textarea = document.createElement('textarea');
    textarea.innerHTML = match[1];

    try {
      return new URL(textarea.value, window.location.origin).href;
    } catch (_) {
      return '';
    }
  }

  function warmFacetRuntimeFromHtml(html) {
    if (customElements.get('facet-filters-form')) {
      return Promise.resolve();
    }

    if (facetRuntimeWarmPromise) {
      return facetRuntimeWarmPromise;
    }

    const src = extractFacetRuntimeSrc(html);
    if (!src) return Promise.resolve();

    const existing = Array.from(document.scripts).find(
      (script) => script.src === src
    );

    if (existing) {
      facetRuntimeWarmPromise = new Promise((resolve) => {
        if (customElements.get('facet-filters-form')) {
          resolve();
          return;
        }

        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => resolve(), { once: true });

        window.setTimeout(resolve, 1500);
      });

      return facetRuntimeWarmPromise;
    }

    facetRuntimeWarmPromise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = false;
      script.dataset.watchersFacetRuntimeWarm = 'true';
      script.onload = () => resolve();
      script.onerror = () => resolve();
      document.head.appendChild(script);
    });

    return facetRuntimeWarmPromise;
  }

  function resetFacetRuntimeForNavigation(targetUrl) {
    if (typeof FacetFiltersForm === 'undefined') return;

    const searchParams = targetUrl.search.slice(1);

    if (typeof FacetFiltersForm.resetForNavigation === 'function') {
      FacetFiltersForm.resetForNavigation(searchParams);
      return;
    }

    FacetFiltersForm.filterData = [];
    FacetFiltersForm.searchParamsInitial = searchParams;
    FacetFiltersForm.searchParamsPrev = searchParams;
  }

  function pruneNavigationPreloadCache() {
    if (navigationPreloadCache.size <= NAVIGATION_PRELOAD_MAX) return;

    const entries = Array.from(navigationPreloadCache.entries())
      .sort((a, b) => a[1].createdAt - b[1].createdAt);

    while (entries.length && navigationPreloadCache.size > NAVIGATION_PRELOAD_MAX) {
      const [key] = entries.shift();
      navigationPreloadCache.delete(key);
    }
  }

  function getFreshNavigationPreload(url) {
    const entry = navigationPreloadCache.get(url);
    if (!entry) return null;

    if (Date.now() - entry.createdAt > NAVIGATION_PRELOAD_TTL) {
      navigationPreloadCache.delete(url);
      return null;
    }

    return entry.promise;
  }

  function preloadNavigationUrl(value) {
    const url = normalizeUrl(value);
    if (!url || url.origin !== window.location.origin) return null;

    const current = new URL(window.location.href);
    if (url.href === current.href) return null;

    const existing = getFreshNavigationPreload(url.href);
    if (existing) return existing;

    const preloadController = new AbortController();
    const entry = {
      createdAt: Date.now(),
      controller: preloadController,
      settled: false,
      promise: null
    };

    const promise = fetch(url.href, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'Accept': 'text/html,application/xhtml+xml',
        'X-Requested-With': 'WATCHERS-Navigation-Preload'
      },
      signal: preloadController.signal
    })
      .then((response) => {
        const removedProduct404 =
          response.status === 404 &&
          url.pathname.startsWith('/products/');

        if (!response.ok && !removedProduct404) {
          throw new Error(`Navigation preload failed: ${response.status}`);
        }

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('text/html')) {
          throw new Error('Navigation preload response was not HTML');
        }

        return response.text();
      })
      .then((html) => {
        /* Warm Dawn's facet runtime while Home/collection HTML is already
           preloading. This does not change Home navigation behavior. */
        warmFacetRuntimeFromHtml(html)?.catch(() => {});

        /* N41: when a product page is already preloading, start the hero request too. */
        warmProductHeroFromHtml(url.href, html);
        return html;
      })
      .catch((error) => {
        navigationPreloadCache.delete(url.href);
        throw error;
      })
      .finally(() => {
        entry.settled = true;
      });

    entry.promise = promise;
    navigationPreloadCache.set(url.href, entry);

    pruneNavigationPreloadCache();

    return promise;
  }

  function cancelPendingNavigationPreloads(keepHref = '') {
    navigationPreloadCache.forEach((entry, key) => {
      if (key === keepHref || entry?.settled) return;

      try {
        entry.controller?.abort();
      } catch (_) {}

      navigationPreloadCache.delete(key);
    });
  }

  function installBaseStyles() {
    if (document.getElementById('WatchersPersistentNavigationStyles')) return;

    const style = document.createElement('style');
    style.id = 'WatchersPersistentNavigationStyles';

    style.textContent = `
      html,
      body {
        background: #f4f4f1;
      }

      #MainContent {
        background: #f4f4f1;
      }

      html.${NAV_CLASS} {
        cursor: progress;
      }

      html.${NAV_CLASS} #MainContent {
        pointer-events: none;
      }

      #WatchersNavigationProgress {
        position: fixed;
        left: 0;
        right: 0;
        top: 0;
        z-index: 2147483000;
        height: 2px;
        overflow: hidden;
        pointer-events: none;
        opacity: 0;
        transition: opacity 120ms ease;
      }

      #WatchersNavigationProgress::after {
        content: '';
        display: block;
        width: 34%;
        height: 100%;
        background: #111111;
        transform: translateX(-120%);
      }

      html.${NAV_CLASS} #WatchersNavigationProgress {
        opacity: 1;
      }

      html.${NAV_CLASS} #WatchersNavigationProgress::after {
        animation: watchersNavigationProgress 760ms ease-in-out infinite;
      }

      @keyframes watchersNavigationProgress {
        0% {
          transform: translateX(-120%);
        }

        100% {
          transform: translateX(390%);
        }
      }

      html.watchers-facet-fast-refresh
        #ProductGridContainer
        .loading-overlay,
      html.watchers-facet-fast-refresh
        #ProductGridContainer
        .loading-overlay__spinner {
        display: none !important;
        opacity: 0 !important;
        visibility: hidden !important;
      }

      html.watchers-facet-fast-refresh
        #ProductGridContainer
        .collection.loading,
      html.watchers-facet-fast-refresh
        #ProductGridContainer
        .collection.loading
        #product-grid,
      html.watchers-facet-fast-refresh
        #ProductGridContainer
        #product-grid.loading {
        opacity: 1 !important;
        visibility: visible !important;
      }

      @media (prefers-reduced-motion: reduce) {
        html.${NAV_CLASS} #WatchersNavigationProgress::after {
          animation: none;
          width: 100%;
          transform: none;
        }
      }
    `;

    document.head.appendChild(style);

    const progress = document.createElement('div');
    progress.id = 'WatchersNavigationProgress';
    progress.setAttribute('aria-hidden', 'true');

    document.body.prepend(progress);
  }

  function normalizeUrl(value) {
    try {
      return new URL(value, window.location.href);
    } catch (_) {
      return null;
    }
  }

  /*
    N45 — REMOVED PRODUCT 404 PRESENTATION

    Scope is intentionally narrow:
    - product pathname only
    - Shopify's genuine .template-404 only
    - no history overrides, no special click routing, no staged rendering
    - CONTINUE SHOPPING is a normal internal Home link
  */
  function applyRemovedProduct404Presentation(root, targetUrl) {
    const url = normalizeUrl(targetUrl);
    if (!url || !url.pathname.startsWith('/products/')) return false;
    if (!root?.querySelector) return false;

    const template404 = root.querySelector('.template-404');
    if (!template404) return false;

    const title = template404.querySelector('h1, .title');
    const message = template404.querySelector('p');
    const link = template404.querySelector('a[href]');

    if (title) title.textContent = 'ITEM SOLD OUT';
    if (message) {
      message.textContent = 'This item has already been sold and is no longer available.';
    }

    if (title && message && title.nextElementSibling !== message) {
      title.insertAdjacentElement('afterend', message);
    }

    if (link) {
      link.href = '/';
      link.textContent = 'CONTINUE SHOPPING';
    }

    return true;
  }

  /*
    N35 — PAGINATION RESTORE (surgically restored from the proven pagination
    baseline; no unrelated navigation logic is rolled back).
  */
  function isPaginationAnchor(anchor) {
    if (!anchor?.href) return false;
    if (anchor.closest('.pagination-wrapper, .pagination')) return true;

    const target = normalizeUrl(anchor.href);
    const current = new URL(window.location.href);
    if (!target) return false;

    return (
      target.pathname === current.pathname &&
      target.searchParams.has('page') &&
      target.searchParams.get('page') !== current.searchParams.get('page')
    );
  }

  function stabilizeTopAfterPagination() {
    const restore = () =>
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    restore();
    requestAnimationFrame(() => requestAnimationFrame(restore));
    window.setTimeout(restore, 80);
    window.setTimeout(restore, 180);
    window.setTimeout(restore, 360);
  }

  function markPaginationSource(targetHref) {
    const state = {
      ...(history.state || {}),
      watchersScrollX: window.scrollX,
      watchersScrollY: window.scrollY,
      watchersPaginationSource: true,
      watchersPaginationTarget: String(targetHref || '')
    };

    history.replaceState(state, '', window.location.href);
  }

  function canonicalBrandCardDestination(anchor) {
    if (!anchor?.matches?.('[data-watchers-brand-card][href]')) return anchor?.href || '';

    const param = String(anchor.dataset.watchersBrandParam || '').trim();
    const value = String(anchor.dataset.watchersBrandValue || '').trim();
    const url = normalizeUrl(anchor.href);

    if (!url || !param || !value) return anchor.href;

    url.search = '';
    url.searchParams.append(param, value);
    url.hash = '';
    return url.href;
  }

  function isExcludedPath(pathname) {
    return EXCLUDED_PREFIXES.some(
      (prefix) =>
        pathname === prefix ||
        pathname.startsWith(`${prefix}/`)
    );
  }

  function isEligibleAnchor(anchor, event) {
    if (!anchor || !(anchor instanceof HTMLAnchorElement)) return false;
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;

    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return false;
    }

    if (anchor.hasAttribute('download')) return false;

    if (
      anchor.target &&
      anchor.target.toLowerCase() !== '_self'
    ) {
      return false;
    }

    if (anchor.hasAttribute('data-no-watchers-nav')) return false;
    if (anchor.closest('[data-no-watchers-nav]')) return false;

    if (
      anchor.id === 'cart-icon-bubble' ||
      anchor.closest('#cart-icon-bubble')
    ) {
      return false;
    }

    if (
      anchor.closest(
        '#main-collection-filters, ' +
        '.mobile-facets, ' +
        '.active-facets, ' +
        '.active-facets-mobile'
      )
    ) {
      return false;
    }

    const rawHref = anchor.getAttribute('href');

    if (!rawHref || rawHref.startsWith('#')) return false;

    if (/^(mailto:|tel:|sms:|javascript:)/i.test(rawHref)) {
      return false;
    }

    const url = normalizeUrl(anchor.href);

    if (!url) return false;
    if (!/^https?:$/.test(url.protocol)) return false;
    if (url.origin !== window.location.origin) return false;
    if (isExcludedPath(url.pathname)) return false;

    const current = new URL(window.location.href);

    const sameDocument =
      url.pathname === current.pathname &&
      url.search === current.search &&
      url.hash;

    if (sameDocument) return false;

    return true;
  }

  function setLoading(active) {
    document.documentElement.classList.toggle(
      NAV_CLASS,
      active
    );

    document.documentElement.setAttribute(
      'aria-busy',
      active ? 'true' : 'false'
    );
  }

  function closeTransientUi() {
    /*
      N27 — PERSISTENT HEADER STATE RESET

      The announcement/header stay mounted while #MainContent changes. Dawn's
      drawer/search custom elements therefore also stay mounted and can carry an
      in-progress submenu, search modal, scroll lock, or menu-opening class into
      the next page. Reset the persistent header to its true root/closed state
      before every navigation swap.
    */

    try {
      const cartDrawer = document.querySelector('cart-drawer');

      if (
        cartDrawer &&
        typeof cartDrawer.close === 'function' &&
        cartDrawer.classList.contains('active')
      ) {
        cartDrawer.close();
      }
    } catch (_) {}

    const headerSection =
      document.querySelector('.section-header');

    /* Close Dawn search through its own API first so focus trapping is released. */
    headerSection
      ?.querySelectorAll('details-modal')
      .forEach((modal) => {
        try {
          if (typeof modal.close === 'function') {
            modal.close(false);
          }
        } catch (_) {}
      });

    const headerDrawer =
      headerSection?.querySelector('header-drawer');

    const mainDrawerDetails =
      headerSection?.querySelector('#Details-menu-drawer-container');

    const drawerSummary =
      mainDrawerDetails?.querySelector(':scope > summary');

    /* Use Dawn's close path when available, then perform an immediate root reset. */
    try {
      if (
        headerDrawer &&
        mainDrawerDetails?.open &&
        typeof headerDrawer.closeMenuDrawer === 'function' &&
        drawerSummary
      ) {
        headerDrawer.closeMenuDrawer(
          new Event('watchers-navigation-close'),
          drawerSummary
        );
      }
    } catch (_) {}

    if (headerSection) {
      headerSection
        .querySelectorAll('details')
        .forEach((details) => {
          details.open = false;
          details.removeAttribute('open');
          details.classList.remove('menu-opening');

          const summary =
            details.querySelector(':scope > summary');

          summary?.setAttribute('aria-expanded', 'false');
        });

      headerSection
        .querySelectorAll('.menu-opening, .submenu-open')
        .forEach((element) => {
          element.classList.remove('menu-opening', 'submenu-open');
        });

      headerSection.classList.remove('menu-open');

      /* Return every drawer panel to the top/root for the next open. */
      headerSection
        .querySelectorAll(
          '#menu-drawer, ' +
          '.menu-drawer__inner-container, ' +
          '.menu-drawer__navigation-container, ' +
          '.menu-drawer__submenu'
        )
        .forEach((element) => {
          element.scrollTop = 0;
          element.scrollLeft = 0;
        });

      /* H07 width-lock is normally released by the details toggle event.
         Clear any stale inline lock immediately as a second safety net. */
      const watchersHeader =
        headerSection.querySelector('.watchers-header');

      if (watchersHeader) {
        watchersHeader.style.removeProperty('width');
        watchersHeader.style.removeProperty('min-width');
        watchersHeader.style.removeProperty('max-width');
        watchersHeader.style.removeProperty('margin-left');
        watchersHeader.style.removeProperty('margin-right');
        watchersHeader.style.removeProperty('transition');
      }
    }

    document.body.classList.remove(
      'overflow-hidden',
      'overflow-hidden-mobile',
      'overflow-hidden-tablet',
      'overflow-hidden-desktop'
    );

    document.documentElement.classList.remove(
      'overflow-hidden',
      'overflow-hidden-mobile',
      'overflow-hidden-tablet',
      'overflow-hidden-desktop'
    );

    if (document.body.style.overflow === 'hidden') {
      document.body.style.removeProperty('overflow');
    }

    if (document.documentElement.style.overflow === 'hidden') {
      document.documentElement.style.removeProperty('overflow');
    }

    document.documentElement.style.removeProperty('--header-bottom-position');
  }

  function copyDocumentMetadata(nextDoc) {
    document.title =
      nextDoc.title ||
      document.title;

    const nextCanonical =
      nextDoc.querySelector('link[rel="canonical"]');

    const currentCanonical =
      document.querySelector('link[rel="canonical"]');

    if (nextCanonical && currentCanonical) {
      currentCanonical.href = nextCanonical.href;
    }

    const nextDescription =
      nextDoc.querySelector('meta[name="description"]');

    let currentDescription =
      document.querySelector('meta[name="description"]');

    if (nextDescription) {
      if (!currentDescription) {
        currentDescription =
          document.createElement('meta');

        currentDescription.name = 'description';

        document.head.appendChild(
          currentDescription
        );
      }

      currentDescription.content =
        nextDescription.content;
    } else if (currentDescription) {
      currentDescription.remove();
    }

    if (nextDoc.documentElement.lang) {
      document.documentElement.lang =
        nextDoc.documentElement.lang;
    }

    if (nextDoc.body) {
      document.body.className =
        nextDoc.body.className;
    }
  }

  function isExecutableScript(script) {
    const type =
      (script.getAttribute('type') || '')
        .trim()
        .toLowerCase();

    return (
      !type ||
      type === 'text/javascript' ||
      type === 'application/javascript' ||
      type === 'module'
    );
  }

  function reviveScripts(root) {
    const existingExternal = new Set(
      Array.from(document.scripts)
        .map((script) => script.src)
        .filter(Boolean)
    );

    root
      .querySelectorAll('script')
      .forEach((oldScript) => {
        if (!isExecutableScript(oldScript)) return;

        const src = oldScript.src;

        if (
          src &&
          existingExternal.has(src)
        ) {
          oldScript.remove();
          return;
        }

        const script =
          document.createElement('script');

        Array.from(
          oldScript.attributes
        ).forEach((attribute) => {
          script.setAttribute(
            attribute.name,
            attribute.value
          );
        });

        if (!src) {
          script.textContent =
            oldScript.textContent;
        } else {
          existingExternal.add(src);
        }

        oldScript.replaceWith(script);
      });
  }

  function refreshHeaderCurrentState(targetUrl) {
    const target =
      new URL(
        targetUrl,
        window.location.href
      );

    document
      .querySelectorAll('.section-header a[href]')
      .forEach((anchor) => {
        const url =
          normalizeUrl(anchor.href);

        if (
          !url ||
          url.origin !== target.origin
        ) {
          return;
        }

        const isCurrent =
          url.pathname.replace(/\/$/, '') ===
            target.pathname.replace(/\/$/, '') &&
          url.search === target.search;

        if (isCurrent) {
          anchor.setAttribute(
            'aria-current',
            'page'
          );
        } else {
          anchor.removeAttribute(
            'aria-current'
          );
        }
      });
  }

  function productHeroUrlFromHtml(html) {
    if (!html || typeof html !== 'string') return '';

    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const img = doc.querySelector(
        'product-info #Slider-Gallery img, ' +
        'product-info .product__media img, ' +
        'product-info media-gallery img'
      );
      if (!img) return '';

      const srcset = String(img.getAttribute('srcset') || '').trim();
      if (srcset) {
        const candidates = srcset
          .split(',')
          .map((part) => part.trim())
          .map((part) => {
            const bits = part.split(/\s+/);
            const width = Number(String(bits[1] || '').replace(/w$/i, '')) || 0;
            return { url: bits[0] || '', width };
          })
          .filter((item) => item.url)
          .sort((a, b) => b.width - a.width);
        if (candidates[0]?.url) {
          return new URL(candidates[0].url, window.location.origin).href;
        }
      }

      const src = img.getAttribute('src');
      return src ? new URL(src, window.location.origin).href : '';
    } catch (_) {
      return '';
    }
  }

  const warmedProductImages = new Set();

  function warmProductHeroFromHtml(targetUrl, html) {
    const url = normalizeUrl(targetUrl);
    if (!url || !url.pathname.startsWith('/products/')) return null;

    const heroUrl = productHeroUrlFromHtml(html);
    if (!heroUrl || warmedProductImages.has(heroUrl)) return null;
    warmedProductImages.add(heroUrl);

    const image = new Image();
    image.decoding = 'async';
    try { image.fetchPriority = 'high'; } catch (_) {}
    image.src = heroUrl;
    return image;
  }

  function prepareImportedProductMedia(nextDoc, targetUrl) {
    const url = normalizeUrl(targetUrl);
    if (!url || !url.pathname.startsWith('/products/')) return;

    const images = Array.from(
      nextDoc.querySelectorAll(
        'product-info #Slider-Gallery img, ' +
        'product-info .product__media img, ' +
        'product-info media-gallery img'
      )
    );

    images.forEach((img, index) => {
      img.setAttribute('loading', 'eager');
      img.setAttribute('decoding', 'async');
      if (index === 0) img.setAttribute('fetchpriority', 'high');
    });
  }

  function startProductMediaWarm(nextDoc, targetUrl) {
    const url = normalizeUrl(targetUrl);
    if (!url || !url.pathname.startsWith('/products/')) return null;

    const images = Array.from(nextDoc.querySelectorAll(
      'product-info #Slider-Gallery img, ' +
      'product-info .product__media img, ' +
      'product-info media-gallery img'
    ));

    if (!images.length) return null;

    const jobs = images.map((img, index) => new Promise((resolve) => {
      const probe = new Image();
      probe.decoding = 'async';
      if (index === 0) {
        try { probe.fetchPriority = 'high'; } catch (_) {}
      }

      const sizes = img.getAttribute('sizes');
      const srcset = img.getAttribute('srcset');
      const src = img.getAttribute('src');
      if (sizes) probe.sizes = sizes;
      if (srcset) probe.srcset = srcset;

      let done = false;
      const finish = async () => {
        if (done) return;
        done = true;
        try { await probe.decode(); } catch (_) {}
        resolve();
      };

      probe.onload = finish;
      probe.onerror = finish;
      if (src) probe.src = src;
      else finish();
    }));

    return {
      hero: jobs[0],
      all: Promise.allSettled(jobs)
    };
  }

  function emitSectionLoadEvents(root) {
    root
      .querySelectorAll('.shopify-section')
      .forEach((section) => {
        document.dispatchEvent(
          new CustomEvent(
            'shopify:section:load',
            {
              bubbles: true,
              detail: {
                sectionId:
                  section.id.replace(
                    'shopify-section-',
                    ''
                  )
              }
            }
          )
        );
      });

    document.dispatchEvent(
      new CustomEvent(
        'watchers:navigation:complete',
        {
          detail: {
            url: window.location.href
          }
        }
      )
    );
  }

  function scrollAfterNavigation(
    url,
    restoreScroll
  ) {
    if (
      restoreScroll &&
      typeof restoreScroll.x === 'number' &&
      typeof restoreScroll.y === 'number'
    ) {
      window.scrollTo(
        restoreScroll.x,
        restoreScroll.y
      );

      return;
    }

    if (url.hash) {
      const id =
        decodeURIComponent(
          url.hash.slice(1)
        );

      const target =
        document.getElementById(id) ||
        document.querySelector(
          `[name="${CSS.escape(id)}"]`
        );

      if (target) {
        target.scrollIntoView();
        return;
      }
    }

    window.scrollTo(0, 0);
  }

  function stabilizePopstateScroll(restoreScroll, restoreState) {
    if (
      !restoreScroll ||
      typeof restoreScroll.x !== 'number' ||
      typeof restoreScroll.y !== 'number'
    ) {
      return;
    }

    const state = restoreState || {};
    const brandName = String(state.watchersBrandDirectoryCard || '').trim();
    const savedCardOffset = Number(state.watchersBrandDirectoryCardOffset);

    const restore = () => {
      if (
        state.watchersBrandDirectoryLanding === true &&
        brandName &&
        Number.isFinite(savedCardOffset)
      ) {
        const card = Array.from(
          document.querySelectorAll('[data-watchers-brand-card]')
        ).find(
          (item) => String(item.dataset.watchersBrandName || '').trim() === brandName
        );

        if (card) {
          const delta = card.getBoundingClientRect().top - savedCardOffset;
          if (Math.abs(delta) > 0.5) {
            window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
          }
          return;
        }
      }

      window.scrollTo({
        top: restoreScroll.y,
        left: restoreScroll.x,
        behavior: 'auto'
      });
    };

    requestAnimationFrame(() => requestAnimationFrame(restore));
    window.setTimeout(restore, 80);
    window.setTimeout(restore, 180);
  }

  async function navigate(
    destination,
    options = {}
  ) {
    const url =
      normalizeUrl(destination);

    if (!url) return;

    const currentBeforeNavigation = new URL(window.location.href);

    /* Freeze the source entry before fetch/DOM replacement can change layout. */
    if (!options.fromPopState) {
      history.replaceState(
        {
          ...(history.state || {}),
          watchersScrollX: window.scrollX,
          watchersScrollY: window.scrollY
        },
        '',
        window.location.href
      );
    }

    if (
      url.href !== currentBeforeNavigation.href &&
      (url.pathname.startsWith('/collections/') || currentBeforeNavigation.pathname.startsWith('/collections/'))
    ) {
      stopPreviousPageWarmWork(url.href);
    }

    if (controller) {
      controller.abort();
    }

    controller =
      new AbortController();

    navigating = true;
    lastRequestedUrl = url.href;

    setLoading(true);

    /*
      N40: normal navigation still closes transient header UI immediately.
      Header-search submissions are the one exception: keep the search panel
      mounted while Shopify prepares the destination so the customer never sees
      the old page waiting underneath. The existing closeTransientUi() call
      immediately before the MainContent swap still closes Search once the new
      result HTML is fully ready.
    */
    if (!options.preserveHeaderSearchUntilReady) {
      closeTransientUi();
    }

    const currentMain =
      document.querySelector(
        MAIN_SELECTOR
      );

    if (!currentMain) {
      window.location.assign(
        url.href
      );

      return;
    }

    try {
      const prefetchedHtmlPromise =
        getFreshNavigationPreload(
          url.href
        );

      let html;

      if (prefetchedHtmlPromise) {
        html = await prefetchedHtmlPromise;
      } else {
        const response =
          await fetch(
            url.href,
            {
              method: 'GET',
              credentials: 'same-origin',

              headers: {
                'Accept':
                  'text/html,application/xhtml+xml',

                'X-Requested-With':
                  'WATCHERS-Persistent-Navigation'
              },

              signal:
                controller.signal
            }
          );

        const removedProduct404 =
          response.status === 404 &&
          url.pathname.startsWith('/products/');

        if (!response.ok && !removedProduct404) {
          throw new Error(
            `Navigation request failed: ${response.status}`
          );
        }

        const contentType =
          response.headers.get(
            'content-type'
          ) || '';

        if (
          !contentType.includes(
            'text/html'
          )
        ) {
          throw new Error(
            'Navigation response was not HTML'
          );
        }

        html =
          await response.text();
      }

      /* If the customer beats the background warmup, finish loading facets.js
         before activating a collection page. This prevents the first drawer or
         quicklink interaction from occurring before FacetFiltersForm exists. */
      await warmFacetRuntimeFromHtml(html);

      const nextDoc =
        new DOMParser()
          .parseFromString(
            html,
            'text/html'
          );

      applyRemovedProduct404Presentation(
        nextDoc,
        url.href
      );

      /* N42: keep PDP media eager, but never hold navigation for a full image decode.
         Give the already-started hero request only a short head start so product
         navigation stays responsive while avoiding the old blank-photo flash. */
      prepareImportedProductMedia(nextDoc, url);
      const productMediaWarm = startProductMediaWarm(nextDoc, url);
      if (productMediaWarm?.hero) {
        await Promise.race([
          productMediaWarm.hero,
          new Promise((resolve) => window.setTimeout(resolve, 160))
        ]);
      }

      const nextMain =
        nextDoc.querySelector(
          MAIN_SELECTOR
        );

      if (!nextMain) {
        throw new Error(
          'Next page does not contain #MainContent'
        );
      }

      if (
        url.href !==
        lastRequestedUrl
      ) {
        return;
      }

      closeTransientUi();
      copyDocumentMetadata(nextDoc);

      const importedMain =
        document.importNode(
          nextMain,
          true
        );

      /* Reset Dawn facet cache/history state to the destination collection
         before the imported facet custom elements become interactive. */
      resetFacetRuntimeForNavigation(url);

      currentMain.replaceWith(
        importedMain
      );

      reviveScripts(importedMain);

      if (!options.fromPopState) {
        history.pushState(
          {
            watchersNav: true,
            ...(options.pushStateData || {})
          },

          '',

          url.href
        );
      }

      refreshHeaderCurrentState(
        url.href
      );

      /*
        After a normal navigation into ALL BRANDS -> selected Brand, the newly
        inserted collection nav contains the canonical Brand context. Store it
        on THIS history entry so phone/browser Back between smart states can
        always restore the same Brand.
      */
      const activeBrandContext =
        getActiveAllBrandsBrandContext();

      if (activeBrandContext) {
        history.replaceState(
          {
            ...(history.state || {}),
            watchersBrandContext: {
              path: activeBrandContext.path,
              param: activeBrandContext.param,
              values: [...activeBrandContext.values]
            }
          },
          '',
          window.location.href
        );
      }

      scrollAfterNavigation(
        url,
        options.restoreScroll || null
      );

      emitSectionLoadEvents(
        importedMain
      );

      if (options.forceTop) {
        stabilizeTopAfterPagination();
      }

      if (options.fromPopState && options.restoreScroll) {
        stabilizePopstateScroll(
          options.restoreScroll,
          options.restoreState || null
        );
      }
    } catch (error) {
      if (
        error &&
        error.name === 'AbortError'
      ) {
        return;
      }

      console.error(
        '[WATCHERS Navigation]',
        error
      );

      window.location.assign(
        url.href
      );

      return;
    } finally {
      if (
        url.href ===
        lastRequestedUrl
      ) {
        navigating = false;
        setLoading(false);
      }
    }
  }

  /*
    N18.5 — SMART COLLECTION QUICKLINK FAST PATH + PERSISTENT REUSABLE CACHE

    Scope:
    - ONLY .watchers-collection-quick-filter links.
    - ONLY when the target stays on the SAME collection pathname.
    - Different collection/page paths continue through the existing N18
      persistent navigation unchanged.

    Behavior:
    - Fetch only the current collection section using Shopify section rendering.
    - Keep the current products visible while the response is travelling.
    - Let Dawn's FacetFiltersForm render the filters/product grid so its
      native facet lifecycle stays intact.
    - Replace the smart quicklink row from the same section response.
    - Abort an older quicklink request if the customer taps a newer one.
    - Do not change the mobile filter runtime below this navigation block.
  */

  let smartQuicklinkController = null;
  let smartQuicklinkRequestId = 0;

  const smartQuicklinkPreloadCache = new Map();
  const SMART_QUICKLINK_PRELOAD_TTL = 2 * 60 * 1000;
  const SMART_QUICKLINK_PRELOAD_MAX = 32;

  /*
    N32 — ALL BRANDS SELECTED-BRAND HISTORY GUARD

    The All Brands selected-brand page is one collection pathname with a Brand
    facet acting as PAGE CONTEXT. Every same-path smart quicklink and every
    browser-Back smart state must carry that Brand parameter.

    This is deliberately read from the currently rendered collection quicklink
    nav. Normal collections do not expose these data attributes, so they are
    completely unaffected.
  */
  function getActiveAllBrandsBrandContext() {
    const nav = document.querySelector(
      '.watchers-collection-quick-filters' +
      '[data-watchers-root="all-brands"]' +
      '[data-watchers-brand-context-param]' +
      '[data-watchers-brand-context-value]'
    );

    if (!nav) return null;

    const param =
      String(
        nav.dataset.watchersBrandContextParam || ''
      ).trim();

    const value =
      String(
        nav.dataset.watchersBrandContextValue || ''
      ).trim();

    if (!param || !value) return null;

    return {
      path: window.location.pathname,
      param,
      values: [value]
    };
  }

  function applyBrandContextToUrl(url, context = getActiveAllBrandsBrandContext()) {
    if (!url || !context) return url;
    if (url.pathname !== context.path) return url;

    url.searchParams.delete(context.param);

    context.values.forEach((value) => {
      url.searchParams.append(
        context.param,
        value
      );
    });

    return url;
  }

  function getBrandContextHistoryMarker() {
    const live =
      getActiveAllBrandsBrandContext();

    if (live) return live;

    const stored =
      history.state?.watchersBrandContext;

    if (
      stored &&
      stored.path === window.location.pathname &&
      stored.param &&
      Array.isArray(stored.values) &&
      stored.values.length
    ) {
      return {
        path: stored.path,
        param: stored.param,
        values: [...stored.values]
      };
    }

    return null;
  }

  function restoreBrandContextForPopState(event) {
    const marker =
      event.state?.watchersBrandContext;

    if (
      !marker ||
      !marker.path ||
      !marker.param ||
      !Array.isArray(marker.values) ||
      !marker.values.length ||
      marker.path !== window.location.pathname
    ) {
      return;
    }

    const url =
      new URL(
        window.location.href
      );

    const currentValues =
      url.searchParams.getAll(
        marker.param
      );

    const alreadyCorrect =
      marker.values.length === currentValues.length &&
      marker.values.every(
        (value) => currentValues.includes(value)
      );

    if (alreadyCorrect) return;

    url.searchParams.delete(
      marker.param
    );

    marker.values.forEach(
      (value) => {
        url.searchParams.append(
          marker.param,
          value
        );
      }
    );

    /*
      Dawn's FacetFiltersForm uses state.searchParams during collection Back.
      Keep BOTH the browser URL and that state payload in sync before Dawn's
      own popstate listener runs.
    */
    if (
      event.state &&
      Object.prototype.hasOwnProperty.call(
        event.state,
        'searchParams'
      )
    ) {
      try {
        event.state.searchParams =
          url.search.slice(1);
      } catch (_) {}
    }

    history.replaceState(
      {
        ...(event.state || {}),
        watchersBrandContext: {
          path: marker.path,
          param: marker.param,
          values: [...marker.values]
        },
        ...(
          event.state &&
          Object.prototype.hasOwnProperty.call(
            event.state,
            'searchParams'
          )
            ? { searchParams: url.search.slice(1) }
            : {}
        )
      },
      '',
      url.href
    );
  }

  function getSmartQuicklinkSectionInfo(anchor) {
    if (!anchor) return null;

    const targetUrl = normalizeUrl(anchor.href);
    if (!targetUrl) return null;

    const currentUrl = new URL(window.location.href);

    /*
      If this is an All Brands selected-brand page, force the current Brand
      context into the quicklink target BEFORE preload/fetch/cache/history.
    */
    applyBrandContextToUrl(
      targetUrl
    );

    if (
      targetUrl.origin !== currentUrl.origin ||
      targetUrl.pathname !== currentUrl.pathname
    ) {
      return null;
    }

    const productGrid = document.getElementById('product-grid');
    const filters = document.getElementById('main-collection-filters');
    const sectionId =
      productGrid?.dataset?.id ||
      filters?.dataset?.id ||
      '';

    if (!sectionId) return null;

    const fetchUrl = new URL(targetUrl.href);
    fetchUrl.searchParams.set('section_id', sectionId);

    return { targetUrl, fetchUrl, sectionId };
  }

  function pruneSmartQuicklinkPreloadCache() {
    if (smartQuicklinkPreloadCache.size <= SMART_QUICKLINK_PRELOAD_MAX) return;

    const entries = Array.from(smartQuicklinkPreloadCache.entries())
      .sort((a, b) => a[1].createdAt - b[1].createdAt);

    while (entries.length && smartQuicklinkPreloadCache.size > SMART_QUICKLINK_PRELOAD_MAX) {
      const [key] = entries.shift();
      smartQuicklinkPreloadCache.delete(key);
    }
  }

  function cacheCurrentSmartQuicklinkSection(sectionId) {
    if (!sectionId) return;

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('section_id', sectionId);

    const sectionRoot =
      document.getElementById('product-grid')
        ?.closest('[id^="shopify-section-"]') ||
      document.getElementById('main-collection-filters')
        ?.closest('[id^="shopify-section-"]');

    if (!sectionRoot) return;

    const html = sectionRoot.outerHTML;

    smartQuicklinkPreloadCache.set(currentUrl.href, {
      createdAt: Date.now(),
      promise: Promise.resolve(html)
    });

    pruneSmartQuicklinkPreloadCache();
  }

  function getFreshSmartQuicklinkPreload(fetchUrl) {
    const key = fetchUrl.href;
    const entry = smartQuicklinkPreloadCache.get(key);

    if (!entry) return null;

    if (Date.now() - entry.createdAt > SMART_QUICKLINK_PRELOAD_TTL) {
      smartQuicklinkPreloadCache.delete(key);
      return null;
    }

    return entry.promise;
  }

  function preloadSmartQuicklink(anchor) {
    const info = getSmartQuicklinkSectionInfo(anchor);
    if (!info) return null;

    const existing = getFreshSmartQuicklinkPreload(info.fetchUrl);
    if (existing) return existing;

    const preloadController = new AbortController();
    const entry = {
      createdAt: Date.now(),
      controller: preloadController,
      settled: false,
      promise: null
    };

    const promise = fetch(info.fetchUrl.href, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'WATCHERS-Smart-Quicklink-Preload'
      },
      signal: preloadController.signal
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Smart quicklink preload failed: ${response.status}`);
        }
        return response.text();
      })
      .catch((error) => {
        smartQuicklinkPreloadCache.delete(info.fetchUrl.href);
        throw error;
      })
      .finally(() => {
        entry.settled = true;
      });

    entry.promise = promise;
    smartQuicklinkPreloadCache.set(info.fetchUrl.href, entry);

    pruneSmartQuicklinkPreloadCache();

    return promise;
  }

  function cancelPendingSmartQuicklinkPreloads(keepHref = '') {
    smartQuicklinkPreloadCache.forEach((entry, key) => {
      if (key === keepHref || entry?.settled) return;

      try {
        entry.controller?.abort();
      } catch (_) {}

      smartQuicklinkPreloadCache.delete(key);
    });
  }

  const collectionWarmQueue = [];
  const collectionWarmQueued = new Set();
  let collectionWarmActive = 0;
  const COLLECTION_WARM_CONCURRENCY = 6;

  function isCollectionNavigationAnchor(anchor) {
    if (!anchor || !anchor.href) return false;

    const targetUrl = normalizeUrl(anchor.href);
    if (!targetUrl || targetUrl.origin !== window.location.origin) return false;

    return targetUrl.pathname === '/collections' || targetUrl.pathname.startsWith('/collections/');
  }


  function isAllBrandsDestination(anchor) {
    if (!anchor || !anchor.href) return false;

    const targetUrl = normalizeUrl(anchor.href);
    if (!targetUrl || targetUrl.origin !== window.location.origin) return false;

    const path = targetUrl.pathname.replace(/\/$/, '').toLowerCase();
    return path === '/collections/all-brand' || path === '/collections/all-brands';
  }

  function runCollectionWarmQueue() {
    while (collectionWarmActive < COLLECTION_WARM_CONCURRENCY && collectionWarmQueue.length) {
      const anchor = collectionWarmQueue.shift();
      if (!anchor || !anchor.isConnected) continue;

      const targetUrl = normalizeUrl(anchor.href);
      if (!targetUrl) continue;

      collectionWarmQueued.delete(targetUrl.href);
      collectionWarmActive += 1;

      const currentUrl = new URL(window.location.href);
      const work =
        anchor.matches('.watchers-collection-quick-filter') &&
        targetUrl.pathname === currentUrl.pathname
          ? preloadSmartQuicklink(anchor)
          : preloadNavigationUrl(targetUrl.href);

      Promise.resolve(work)
        .catch(() => {})
        .finally(() => {
          collectionWarmActive = Math.max(0, collectionWarmActive - 1);
          runCollectionWarmQueue();
        });
    }
  }

  function enqueueCollectionWarm(anchor) {
    if (!isCollectionNavigationAnchor(anchor)) return;

    const targetUrl = normalizeUrl(anchor.href);
    if (!targetUrl) return;

    if (collectionWarmQueued.has(targetUrl.href)) return;

    if (
      getFreshNavigationPreload(targetUrl.href) ||
      (
        anchor.matches('.watchers-collection-quick-filter') &&
        (() => {
          const info = getSmartQuicklinkSectionInfo(anchor);
          return info ? getFreshSmartQuicklinkPreload(info.fetchUrl) : null;
        })()
      )
    ) {
      return;
    }

    collectionWarmQueued.add(targetUrl.href);
    collectionWarmQueue.push(anchor);
    runCollectionWarmQueue();
  }

  function prioritizeCollectionWarm(anchor) {
    if (!isCollectionNavigationAnchor(anchor)) return;

    const targetUrl = normalizeUrl(anchor.href);
    if (!targetUrl) return;

    const currentUrl = new URL(window.location.href);

    if (
      anchor.matches('.watchers-collection-quick-filter') &&
      targetUrl.pathname === currentUrl.pathname
    ) {
      // N23: same-collection quicklinks are facet actions now.
      return;
    }

    /*
      Customer intent wins over the background queue. Starting the exact target
      immediately means a quick tap does not wait behind other collection warms.
    */
    if (anchor.matches('[data-watchers-brand-card]')) {
      collectionWarmQueue.length = 0;
      collectionWarmQueued.clear();
      cancelPendingNavigationPreloads(targetUrl.href);
    }

    preloadNavigationUrl(targetUrl.href)?.catch(() => {});
  }

  function stopPreviousPageWarmWork(keepNavigationHref = '') {
    collectionWarmQueue.length = 0;
    collectionWarmQueued.clear();
    cancelPendingNavigationPreloads(keepNavigationHref);
    cancelPendingSmartQuicklinkPreloads();
  }

  /*
    N43 — TARGETED VISIBLE PRODUCT PRELOAD

    Scope is intentionally conservative:
    - collection and search result pages only
    - only product links already visible or just below the viewport
    - maximum four unique product destinations per warm pass
    - maximum two preload requests at once
    - reuses the existing navigation preload cache and N41 hero-image warmer

    This avoids preloading an entire result set while giving the first likely
    product taps a head start before the customer actually presses a card.
  */
  const PRODUCT_WARM_CONCURRENCY = 2;
  const PRODUCT_WARM_MAX_PER_PASS = 4;
  const productWarmQueue = [];
  const productWarmQueued = new Set();
  let productWarmActive = 0;

  function isProductNavigationAnchor(anchor) {
    if (!anchor?.href) return false;

    const targetUrl = normalizeUrl(anchor.href);
    if (!targetUrl || targetUrl.origin !== window.location.origin) return false;

    return targetUrl.pathname.startsWith('/products/');
  }

  function runProductWarmQueue() {
    while (
      productWarmActive < PRODUCT_WARM_CONCURRENCY &&
      productWarmQueue.length
    ) {
      const href = productWarmQueue.shift();
      if (!href) continue;

      if (getFreshNavigationPreload(href)) continue;

      productWarmActive += 1;

      const work = preloadNavigationUrl(href);

      Promise.resolve(work)
        .catch(() => {})
        .finally(() => {
          productWarmActive = Math.max(0, productWarmActive - 1);
          runProductWarmQueue();
        });
    }
  }

  function enqueueProductWarm(anchor) {
    if (!isProductNavigationAnchor(anchor)) return;

    const targetUrl = normalizeUrl(anchor.href);
    if (!targetUrl) return;

    if (
      productWarmQueued.has(targetUrl.href) ||
      getFreshNavigationPreload(targetUrl.href)
    ) {
      return;
    }

    productWarmQueued.add(targetUrl.href);
    productWarmQueue.push(targetUrl.href);
    runProductWarmQueue();
  }

  function warmVisibleProductCards(root = document) {
    const path = window.location.pathname;
    if (!(path.startsWith('/collections/') || path === '/search')) return;
    if (!root?.querySelectorAll) return;

    const scope = root === document ? document : root;
    const unique = new Map();

    scope
      .querySelectorAll(
        '#product-grid a[href], ' +
        '#ProductGridContainer a[href]'
      )
      .forEach((anchor) => {
        if (!isProductNavigationAnchor(anchor)) return;

        const targetUrl = normalizeUrl(anchor.href);
        if (!targetUrl || unique.has(targetUrl.href)) return;

        unique.set(targetUrl.href, anchor);
      });

    const candidates = Array.from(unique.values())
      .map((anchor) => {
        const rect = anchor.getBoundingClientRect();
        const visible = rect.bottom > 0 && rect.top < window.innerHeight;
        const near = rect.bottom > -160 && rect.top < window.innerHeight + 700;

        return {
          anchor,
          priority: visible ? 0 : near ? 1 : 2,
          distance: Math.abs(rect.top)
        };
      })
      .filter((item) => item.priority < 2)
      .sort((a, b) => {
        if (a.priority !== b.priority) return a.priority - b.priority;
        return a.distance - b.distance;
      })
      .slice(0, PRODUCT_WARM_MAX_PER_PASS);

    candidates.forEach(({ anchor }) => enqueueProductWarm(anchor));
  }

  function removeCollectionBackQuicklinks(root = document) {
    if (!root?.querySelectorAll) return;

    root
      .querySelectorAll('.watchers-collection-quick-filter--back')
      .forEach((anchor) => anchor.remove());
  }

  function warmCurrentCollectionQuicklinks(root = document) {
    if (!window.location.pathname.startsWith('/collections/')) return false;
    if (!root?.querySelectorAll) return true;

    removeCollectionBackQuicklinks(root);

    const currentUrl = new URL(window.location.href);
    const quickLinks = Array.from(
      (root === document ? document : root).querySelectorAll(
        '.watchers-collection-quick-filter[href]'
      )
    );

    const unique = new Map();

    quickLinks.forEach((anchor) => {
      const targetUrl = normalizeUrl(anchor.href);
      if (!targetUrl || targetUrl.origin !== window.location.origin) return;
      if (!unique.has(targetUrl.href)) unique.set(targetUrl.href, anchor);
    });

    unique.forEach((anchor) => {
      const targetUrl = normalizeUrl(anchor.href);
      if (!targetUrl) return;

      if (targetUrl.pathname === currentUrl.pathname) {
        // N23: same-path quicklinks use Dawn's facet engine, exactly like the drawer.
        return;
      }

      preloadNavigationUrl(targetUrl.href)?.catch(() => {});
    });

    return true;
  }

  function warmVisibleNavigationLinks(root = document) {
    if (!root?.querySelectorAll) return;

    /* N43: warm only the first visible/near-visible PDP destinations. */
    warmVisibleProductCards(root);

    /*
      N27 — HOME IS ALWAYS HOT
      The WATCHERS logo is persistent and can be pressed from product, page, or
      collection routes. Keep HOME preloaded on every non-home page.
    */
    if (window.location.pathname !== '/') {
      preloadNavigationUrl(`${window.location.origin}/`)?.catch(() => {});
    }

    /*
      N21 — COLLECTION ISOLATION

      Home/general-page warmup below is intentionally left unchanged.
      On collection pages, only the 2–6 currently rendered WATCHERS quicklinks
      are warmed. They do not enter the broad global queue.
    */
    if (window.location.pathname.startsWith('/collections/')) {
      warmCurrentCollectionQuicklinks(root);

      /*
        N28 — ALL BRANDS DIRECTORY USES THE SAME GLOBAL NAVIGATION CACHE

        The old directory owned a second fetch/cache/router. That competed with
        persistent navigation and made lower Brand cards slower and Back restore
        inconsistent. When the directory is present, warm every Brand destination
        through this one global cache instead. Visible/near cards enter first.
      */
      const brandDirectory = document.querySelector(
        '[data-watchers-all-brands-directory]'
      );

      if (brandDirectory) {
        const brandCards = Array.from(
          brandDirectory.querySelectorAll(
            '[data-watchers-brand-card][href]'
          )
        ).filter(isCollectionNavigationAnchor);

        brandCards.sort((a, b) => {
          const aRect = a.getBoundingClientRect();
          const bRect = b.getBoundingClientRect();
          const aNear = aRect.bottom > -500 && aRect.top < window.innerHeight + 900 ? 0 : 1;
          const bNear = bRect.bottom > -500 && bRect.top < window.innerHeight + 900 ? 0 : 1;
          if (aNear !== bNear) return aNear - bNear;
          return Math.abs(aRect.top) - Math.abs(bRect.top);
        });

        brandCards.forEach(enqueueCollectionWarm);
      }

      /*
        Keep WATCHERS logo -> HOME ready while browsing a collection.
        This is a single destination preload and does not re-enable the broad
        previous-page Home queue.
      */
      preloadNavigationUrl(`${window.location.origin}/`)?.catch(() => {});
      return;
    }

    /*
      N22 — HOME HERO FIRST

      Home keeps the proven global collection warmup, but the hero destination
      gets the first request on a fresh activation. The rest of Home starts as
      soon as that first hero request settles. This reduces the common case where
      the customer's very first CTA beats the generic preload queue.
    */
    if (window.location.pathname === '/') {
      const collectionLinks = Array.from(
        (root === document ? document : root).querySelectorAll('a[href]')
      ).filter(isCollectionNavigationAnchor);

      const unique = new Map();
      collectionLinks.forEach((anchor) => {
        const targetUrl = normalizeUrl(anchor.href);
        if (!targetUrl || targetUrl.origin !== window.location.origin) return;
        if (!unique.has(targetUrl.href)) unique.set(targetUrl.href, anchor);
      });

      const links = Array.from(unique.values());
      const main = document.querySelector(MAIN_SELECTOR);

      /*
        N28 — ALL BRANDS IS A FIRST-CLASS HOME DESTINATION

        Do not leave ALL BRANDS behind the hero warmup. Start it immediately so
        the homepage OTHER BRANDS -> ALL BRANDS press can reuse an in-flight or
        completed global navigation cache entry.
      */
      const priorityHrefSet = new Set();

      links
        .filter(isAllBrandsDestination)
        .slice(0, 1)
        .forEach((anchor) => {
          const targetUrl = normalizeUrl(anchor.href);
          if (!targetUrl) return;

          priorityHrefSet.add(targetUrl.href);
          preloadNavigationUrl(targetUrl.href)?.catch(() => {});
        });

      const looksLikeHeroLink = (anchor) => {
        let node = anchor;
        while (node && node !== main && node !== document.body) {
          const id = String(node.id || '').toLowerCase();
          const cls = typeof node.className === 'string'
            ? node.className.toLowerCase()
            : '';
          const marker = `${id} ${cls}`;

          if (
            marker.includes('hero') ||
            marker.includes('banner') ||
            marker.includes('slideshow')
          ) {
            return true;
          }

          node = node.parentElement;
        }
        return false;
      };

      let heroLinks = links.filter(looksLikeHeroLink);

      if (!heroLinks.length) {
        heroLinks = links
          .filter((anchor) => {
            if (!main || !main.contains(anchor)) return false;
            if (anchor.matches('.watchers-quick__item')) return false;
            const rect = anchor.getBoundingClientRect();
            return rect.bottom > 0 && rect.top < window.innerHeight * 1.15;
          })
          .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)
          .slice(0, 1);
      }

      const heroHrefSet = new Set(priorityHrefSet);
      const heroPromises = [];

      heroLinks.slice(0, 2).forEach((anchor) => {
        const targetUrl = normalizeUrl(anchor.href);
        if (!targetUrl || heroHrefSet.has(targetUrl.href)) return;

        heroHrefSet.add(targetUrl.href);
        const work = preloadNavigationUrl(targetUrl.href);
        if (work) heroPromises.push(Promise.resolve(work).catch(() => {}));
      });

      const startRemainingHomeWarmup = () => {
        if (window.location.pathname !== '/') return;

        const remaining = links.filter((anchor) => {
          const targetUrl = normalizeUrl(anchor.href);
          return targetUrl && !heroHrefSet.has(targetUrl.href);
        });

        remaining.sort((a, b) => {
          const aRect = a.getBoundingClientRect();
          const bRect = b.getBoundingClientRect();
          const aVisible = aRect.bottom > 0 && aRect.top < window.innerHeight ? 0 : 1;
          const bVisible = bRect.bottom > 0 && bRect.top < window.innerHeight ? 0 : 1;
          if (aVisible !== bVisible) return aVisible - bVisible;

          const aQuick = a.matches('.watchers-quick__item') ? 0 : 1;
          const bQuick = b.matches('.watchers-quick__item') ? 0 : 1;
          if (aQuick !== bQuick) return aQuick - bQuick;

          return Math.abs(aRect.top) - Math.abs(bRect.top);
        });

        remaining.forEach(enqueueCollectionWarm);
      };

      if (heroPromises.length) {
        Promise.allSettled(heroPromises).finally(startRemainingHomeWarmup);
      } else {
        startRemainingHomeWarmup();
      }

      return;
    }

    /*
      N19.0 — GLOBAL COLLECTION WARMUP

      Every same-origin /collections/... anchor anywhere in the storefront DOM
      is eligible. This deliberately does not depend on section-specific classes,
      so future homepage cards, header links, hamburger links, brand/category
      buttons, and collection shortcuts inherit the same acceleration automatically.
    */
    const collectionLinks = Array.from(
      (root === document ? document : root).querySelectorAll('a[href]')
    ).filter(isCollectionNavigationAnchor);

    const unique = new Map();
    collectionLinks.forEach((anchor) => {
      const targetUrl = normalizeUrl(anchor.href);
      if (!targetUrl || targetUrl.origin !== window.location.origin) return;
      if (!unique.has(targetUrl.href)) unique.set(targetUrl.href, anchor);
    });

    const links = Array.from(unique.values());

    /*
      Warm every collection destination, but prioritize BACK, WATCHERS shortcuts,
      and links already visible in the viewport. Six concurrent requests keep the
      queue moving quickly without firing the whole storefront at once.
    */
    links.sort((a, b) => {
      const aBack = a.classList.contains('watchers-collection-quick-filter--back') ? 0 : 1;
      const bBack = b.classList.contains('watchers-collection-quick-filter--back') ? 0 : 1;
      if (aBack !== bBack) return aBack - bBack;

      const aSpecial = a.matches('.watchers-collection-quick-filter, .watchers-quick__item') ? 0 : 1;
      const bSpecial = b.matches('.watchers-collection-quick-filter, .watchers-quick__item') ? 0 : 1;
      if (aSpecial !== bSpecial) return aSpecial - bSpecial;

      const aRect = a.getBoundingClientRect();
      const bRect = b.getBoundingClientRect();
      const aVisible = aRect.bottom > 0 && aRect.top < window.innerHeight ? 0 : 1;
      const bVisible = bRect.bottom > 0 && bRect.top < window.innerHeight ? 0 : 1;
      if (aVisible !== bVisible) return aVisible - bVisible;

      return Math.abs(aRect.top) - Math.abs(bRect.top);
    });

    links.forEach(enqueueCollectionWarm);

    /*
      While the customer is browsing a collection, warm HOME as well so the
      WATCHERS logo can return there without waiting on the network.
    */
    if (window.location.pathname.startsWith('/collections/')) {
      preloadNavigationUrl(`${window.location.origin}/`)?.catch(() => {});
    }
  }

  function scheduleVisibleNavigationWarmup(root = document) {
    /*
      N19.0: start collection warming immediately. Waiting for idle time caused
      the first customer press to beat the preload queue on fast interactions.
    */
    warmVisibleNavigationLinks(root);
  }

  async function runSmartQuicklinkFastPath(anchor, options = {}) {
    if (!anchor) return false;

    const info = getSmartQuicklinkSectionInfo(anchor);
    if (!info) return false;

    const { targetUrl, fetchUrl } = info;

    if (
      typeof FacetFiltersForm === 'undefined' ||
      typeof FacetFiltersForm.renderFilters !== 'function' ||
      typeof FacetFiltersForm.renderProductGridContainer !== 'function' ||
      typeof FacetFiltersForm.renderProductCount !== 'function'
    ) {
      return false;
    }

    /*
      Preserve the CURRENT rendered state before leaving it. This makes BACK
      instant even when the previous state was the original collection page
      rather than a previously prefetched smart-link response.
    */
    if (!options.fromPopState) {
      cacheCurrentSmartQuicklinkSection(info.sectionId);
    }

    /*
      N21: the customer chose this state. Stop every unfinished request that
      belongs to the previous collection state, but keep the selected preload.
    */
    collectionWarmQueue.length = 0;
    collectionWarmQueued.clear();
    cancelPendingNavigationPreloads();
    cancelPendingSmartQuicklinkPreloads(fetchUrl.href);

    if (smartQuicklinkController) {
      smartQuicklinkController.abort();
    }

    smartQuicklinkController = new AbortController();
    const requestId = ++smartQuicklinkRequestId;

    document.documentElement.classList.add(
      'watchers-facet-fast-refresh'
    );

    try {
      const prefetchedHtmlPromise =
        getFreshSmartQuicklinkPreload(fetchUrl);

      let html;

      if (prefetchedHtmlPromise) {
        html = await prefetchedHtmlPromise;
      } else {
        const response = await fetch(fetchUrl.href, {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'Accept': 'text/html',
            'X-Requested-With': 'WATCHERS-Smart-Quicklink'
          },
          signal: smartQuicklinkController.signal
        });

        if (!response.ok) {
          throw new Error(
            `Smart quicklink request failed: ${response.status}`
          );
        }

        html = await response.text();
      }

      if (requestId !== smartQuicklinkRequestId) {
        return true;
      }

      const parsed = new DOMParser().parseFromString(
        html,
        'text/html'
      );

      const nextQuicklinks = parsed.querySelector(
        '[data-watchers-smart-quicklinks]'
      );

      const liveQuicklinks = document.querySelector(
        '[data-watchers-smart-quicklinks]'
      );

      /*
        Use Dawn's own facet renderers for the sensitive filter/product DOM.
        This avoids the C46 regression caused by manually replacing the
        complete native facet source outside Dawn's lifecycle.
      */
      FacetFiltersForm.renderFilters(html, null);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html);

      if (nextQuicklinks && liveQuicklinks) {
        liveQuicklinks.replaceWith(
          document.importNode(nextQuicklinks, true)
        );
      }

      const searchParams = targetUrl.search.slice(1);

      FacetFiltersForm.searchParamsPrev = searchParams;

      if (!options.fromPopState) {
        const brandContext =
          getBrandContextHistoryMarker();

        history.pushState(
          {
            searchParams,
            watchersSmartQuicklink: true,
            ...(brandContext
              ? {
                  watchersBrandContext: {
                    path: brandContext.path,
                    param: brandContext.param,
                    values: [...brandContext.values]
                  }
                }
              : {})
          },
          '',
          `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
        );
      }

      /*
        Existing WATCHERS listeners use this event to reset their URL-backed
        state and re-check the newly rendered collection controls. No scripts
        are revived and #MainContent is not replaced on this fast path.
      */
      document.dispatchEvent(
        new CustomEvent('watchers:navigation:complete', {
          detail: { url: targetUrl.href, smartQuicklink: true }
        })
      );

      /*
        N18.5: start warming the NEXT smart-link level immediately.
        Do not wait for requestIdleCallback here. The newly rendered links
        should begin fetching as soon as this level is on screen, so rapid
        drill-downs can reuse an in-flight or completed cache entry.
      */
      /*
        The new state is now active. Preload only the quicklinks that exist on
        THIS state. Old-state requests were cancelled above.
      */
      warmCurrentCollectionQuicklinks(document);

      return true;
    } catch (error) {
      if (error && error.name === 'AbortError') {
        return true;
      }

      console.error(
        '[WATCHERS Smart Quicklink]',
        error
      );

      return false;
    } finally {
      if (requestId === smartQuicklinkRequestId) {
        document.documentElement.classList.remove(
          'watchers-facet-fast-refresh'
        );
      }
    }
  }

  ['pointerenter', 'touchstart'].forEach((eventName) => {
    document.addEventListener(
      eventName,
      (event) => {
        const anchor = event.target.closest?.(
          '.watchers-collection-quick-filter[href], ' +
          '.watchers-quick__item[href], ' +
          '.header__heading-link[href], ' +
          '.watchers-header__logo-link[href], ' +
          '#Details-menu-drawer-container a[href], ' +
          'header a[href], ' +
          '#MainContent a[href]'
        );

        if (!anchor) return;

        const targetUrl = normalizeUrl(anchor.href);
        if (!targetUrl || targetUrl.origin !== window.location.origin) return;

        const currentUrl = new URL(window.location.href);

        /* N42: product intent warmup. Reuse the existing global navigation cache
           so hovering/touching a product card starts its HTML + hero image early. */
        if (targetUrl.pathname.startsWith('/products/')) {
          preloadNavigationUrl(targetUrl.href)?.catch(() => {});
          return;
        }

        if (
          anchor.matches('.watchers-collection-quick-filter') &&
          targetUrl.pathname === currentUrl.pathname
        ) {
          // N23: filter-style quicklinks are handled by facets.js.
          return;
        }

        if (isCollectionNavigationAnchor(anchor)) {
          prioritizeCollectionWarm(anchor);
          return;
        }

        if (anchor.matches('.header__heading-link, .watchers-header__logo-link')) {
          preloadNavigationUrl(targetUrl.href)?.catch(() => {});
        }
      },
      { capture: true, passive: true }
    );
  });

  /*
    N19.0 — NEW LINKS AUTO-WARM

    Shopify sections and WATCHERS persistent navigation can inject new collection
    links after the initial page load. Observe additions and warm only newly added
    subtrees that actually contain collection anchors.
  */
  const collectionLinkObserver = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (
          (node.matches?.('a[href]') && isCollectionNavigationAnchor(node)) ||
          node.querySelector?.('a[href*="/collections"]')
        ) {
          warmVisibleNavigationLinks(node);
        }
      }
    }
  });

  collectionLinkObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  /*
    When the hamburger drawer is opened, immediately warm every collection link
    available inside it. The normal two-request queue still controls concurrency.
  */
  document.addEventListener(
    'click',
    (event) => {
      const drawerToggle = event.target.closest?.(
        '#Details-menu-drawer-container > summary, ' +
        '#Details-menu-drawer-container summary'
      );

      if (!drawerToggle) return;

      window.requestAnimationFrame(() => {
        warmVisibleNavigationLinks(document);
      });
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => scheduleVisibleNavigationWarmup(document),
      { once: true }
    );
  } else {
    scheduleVisibleNavigationWarmup(document);
  }

  document.addEventListener(
    'watchers:navigation:complete',
    () => {
      /*
        N18.5: every newly rendered collection level immediately warms its
        visible children. This is intentionally not idle-scheduled because
        the next customer tap can happen almost at once.
      */
      warmVisibleNavigationLinks(document);
    }
  );

  /*
    N19.0 — GLOBAL COLLECTION CLICK PRIORITY

    Any collection link anywhere in the site immediately starts/reuses its preload
    before the normal WATCHERS click router decides how to render it.
  */
  document.addEventListener(
    'pointerdown',
    (event) => {
      const anchor = event.target.closest?.('a[href]');
      if (!anchor || !isCollectionNavigationAnchor(anchor)) return;
      prioritizeCollectionWarm(anchor);
    },
    { capture: true, passive: true }
  );

  /*
    N25 — COLLECTION QUICKLINKS USE THE EXACT NATIVE FACET INPUT PATH

    Same-collection WATCHERS quicklinks are filters, not page navigation.
    Instead of calling renderPage() with a null event, find the native Shopify
    facet input represented by the quicklink URL and click that input. This
    gives the quicklink the exact same event path as selecting the same option
    inside the filter drawer, including Dawn's current-facet optimization.

    Different collection path quicklinks are left alone and continue through
    normal WATCHERS collection navigation.
  */
  function getQuicklinkFacetDelta(targetUrl, currentUrl) {
    const currentPairs = new Map();

    currentUrl.searchParams.forEach((value, name) => {
      const key = `${name}\u0000${value}`;
      currentPairs.set(key, (currentPairs.get(key) || 0) + 1);
    });

    const additions = [];

    targetUrl.searchParams.forEach((value, name) => {
      const key = `${name}\u0000${value}`;
      const available = currentPairs.get(key) || 0;

      if (available > 0) {
        currentPairs.set(key, available - 1);
        return;
      }

      additions.push({ name, value });
    });

    return additions;
  }

  function findNativeFacetInputForQuicklink(targetUrl, currentUrl) {
    const additions = getQuicklinkFacetDelta(targetUrl, currentUrl)
      .filter(({ name }) => name.startsWith('filter.'));

    if (!additions.length) return null;

    const roots = [
      document.getElementById('FacetFiltersFormMobile'),
      document.getElementById('FacetFiltersForm'),
      document.getElementById('FacetFiltersPillsForm')
    ].filter(Boolean);

    for (const { name, value } of additions) {
      for (const root of roots) {
        const inputs = root.querySelectorAll(
          'input[type="checkbox"], input[type="radio"], select'
        );

        for (const input of inputs) {
          if (input.name !== name) continue;

          if (input instanceof HTMLSelectElement) {
            const option = Array.from(input.options).find(
              (item) => item.value === value
            );

            if (option) {
              return { input, value, isSelect: true };
            }

            continue;
          }

          if (input.value === value && !input.checked && !input.disabled) {
            return { input, value, isSelect: false };
          }
        }
      }
    }

    return null;
  }

  document.addEventListener(
    'click',

    (event) => {
      if (event.defaultPrevented || event.button !== 0) return;

      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const anchor = event.target.closest?.(
        '.watchers-collection-quick-filter[href]'
      );

      if (!anchor) return;

      const targetUrl = normalizeUrl(anchor.href);
      const currentUrl = new URL(window.location.href);

      if (
        !targetUrl ||
        targetUrl.origin !== currentUrl.origin ||
        targetUrl.pathname !== currentUrl.pathname
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const nativeFacet = findNativeFacetInputForQuicklink(
        targetUrl,
        currentUrl
      );

      if (nativeFacet) {
        const { input, value, isSelect } = nativeFacet;

        if (isSelect) {
          input.value = value;
          input.dispatchEvent(
            new Event('input', { bubbles: true, composed: true })
          );
          input.dispatchEvent(
            new Event('change', { bubbles: true, composed: true })
          );
        } else {
          input.click();
        }

        return;
      }

      /*
        Safe fallback for unusual facet URLs that do not expose a matching
        native input. This remains section-only and never enters persistent
        collection navigation.
      */
      if (
        typeof FacetFiltersForm !== 'undefined' &&
        typeof FacetFiltersForm.renderPage === 'function'
      ) {
        FacetFiltersForm.renderPage(
          targetUrl.search.slice(1),
          null,
          true
        );
        return;
      }

      window.location.assign(targetUrl.href);
    },

    true
  );


  /*
    N36 — IOS PAGINATION HISTORY COMMIT

    Keep the N35 pagination owner, but commit the destination history entry
    synchronously on the click BEFORE any async fetch/DOM work begins. Mobile
    Safari can otherwise collapse or skip an asynchronously-created pagination
    history entry, most noticeably when the NEXT/PREVIOUS arrow is tapped.

    Number links and arrow links now use the exact same history contract.
  */
  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = event.target.closest?.(
        '.pagination-wrapper a[href], .pagination a[href]'
      );
      if (!anchor || !isPaginationAnchor(anchor)) return;

      const targetUrl = normalizeUrl(anchor.href);
      const currentUrl = new URL(window.location.href);
      if (!targetUrl || targetUrl.origin !== currentUrl.origin) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      /* Save the exact source page + scroll position first. */
      markPaginationSource(targetUrl.href);

      /*
        iOS/Safari: create the destination slot synchronously while still inside
        the trusted click event. `navigate(... fromPopState: true)` then performs
        the existing persistent fetch/swap without creating a second entry.
      */
      history.pushState(
        {
          watchersNav: true,
          watchersPaginationEntry: true,
          watchersPaginationFrom: currentUrl.href,
          watchersScrollX: 0,
          watchersScrollY: 0
        },
        '',
        targetUrl.href
      );

      navigate(targetUrl.href, {
        fromPopState: true,
        forceTop: true
      });
    },
    true
  );


  /*
    N39 — PERSISTENT HEADER SEARCH

    The global WATCHERS router intentionally leaves normal forms alone. Header
    search is the one safe exception because it is a same-origin GET navigation
    to Shopify's /search page.

    Keep every other form untouched: product add-to-cart, newsletter, contact,
    account, checkout, filters and app forms continue through their existing
    native owners.
  */
  document.addEventListener(
    'submit',
    (event) => {
      if (event.defaultPrevented) return;

      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      /* Header search only. Never take ownership of a search form in MainContent. */
      const headerOwner = form.closest(
        '.section-header, header, .header-wrapper'
      );
      if (!headerOwner) return;

      const method = String(form.method || 'get').toLowerCase();
      if (method !== 'get') return;

      const actionUrl = normalizeUrl(form.action || '/search');
      if (!actionUrl) return;
      if (actionUrl.origin !== window.location.origin) return;
      if (actionUrl.pathname.replace(/\/$/, '') !== '/search') return;

      /* Match native GET-form behavior: destination query comes from controls. */
      const targetUrl = new URL(actionUrl.href);
      targetUrl.search = '';

      const formData = new FormData(form);
      formData.forEach((value, name) => {
        if (value instanceof File) return;
        targetUrl.searchParams.append(name, String(value));
      });

      event.preventDefault();
      event.stopPropagation();

      navigate(targetUrl.href, {
        preserveHeaderSearchUntilReady: true
      });
    },
    true
  );


  document.addEventListener(
    'click',

    (event) => {
      const anchor =
        event.target.closest &&
        event.target.closest(
          'a[href]'
        );

      if (
        !isEligibleAnchor(
          anchor,
          event
        )
      ) {
        return;
      }

      if (isPaginationAnchor(anchor)) return;

      event.preventDefault();

      navigate(
        canonicalBrandCardDestination(anchor) || anchor.href
      );
    },

    false
  );

  window.addEventListener(
    'popstate',

    (event) => {
      /*
        N32: restore the locked All Brands Brand parameter first, before either
        WATCHERS navigation or Dawn facets reads the Back destination.
      */
      restoreBrandContextForPopState(
        event
      );

      /*
        N37: CART DRAWER has first Back priority globally.

        First popstate:
        - browser moved backward
        - close only CART
        - do not route #MainContent
        - return forward to the exact same page entry

        Second popstate:
        - consume the forward restoration
        - page underneath remains unchanged
      */
      if (restoringAfterCartBack) {
        restoringAfterCartBack = false;
        event.stopImmediatePropagation();

        restoreCartOpenScroll();

        requestAnimationFrame(() => {
          restoreCartOpenScroll();
          releaseCartScrollLock();
        });

        return;
      }

      if (cartDrawerIsOpen()) {
        event.stopImmediatePropagation();
        restoringAfterCartBack = true;

        closeCartDrawerOnly();
        restoreCartOpenScroll();

        window.setTimeout(() => {
          restoreCartOpenScroll();
          history.forward();
        }, 0);

        return;
      }

      /*
        N29: FILTER has first Back priority globally.

        First popstate = browser moved backward. Do NOT route #MainContent. Close
        only FILTER and go forward to the exact collection entry we just left.
        Second popstate = that forward restoration. Consume it too.
      */
      if (restoringAfterFilterBack) {
        restoringAfterFilterBack = false;
        event.stopImmediatePropagation();

        restoreFilterOpenScroll();
        requestAnimationFrame(() => {
          restoreFilterOpenScroll();
          releaseFilterScrollLock();
        });
        return;
      }

      if (mobileFilterIsOpen()) {
        event.stopImmediatePropagation();
        restoringAfterFilterBack = true;

        closeMobileFilterOnly();
        restoreFilterOpenScroll();

        window.setTimeout(() => {
          restoreFilterOpenScroll();
          history.forward();
        }, 0);

        return;
      }

      const restoreScroll = {
        x:
          Number(
            event.state
              ?.watchersScrollX ||
            0
          ),

        y:
          Number(
            event.state
              ?.watchersScrollY ||
            0
          )
      };

      const targetUrl = new URL(window.location.href);

      const collectionDomIsLive = Boolean(
        document.getElementById('ProductGridContainer') &&
        document.getElementById('product-grid')
      );

      const paginationHistoryState = Boolean(
        event.state?.watchersPaginationSource ||
        event.state?.watchersPaginationEntry
      );

      if (
        !paginationHistoryState &&
        collectionDomIsLive &&
        targetUrl.pathname.startsWith('/collections/') &&
        event.state &&
        Object.prototype.hasOwnProperty.call(event.state, 'searchParams') &&
        typeof FacetFiltersForm !== 'undefined'
      ) {
        return;
      }

      event.stopImmediatePropagation();

      navigate(
        targetUrl.href,
        {
          fromPopState: true,
          restoreScroll,
          restoreState: event.state || null
        }
      );
    }
  );

  window.addEventListener(
    'pageshow',

    () => {
      setLoading(false);
      closeTransientUi();
      scheduleVisibleNavigationWarmup(document);
    }
  );

  applyRemovedProduct404Presentation(
    document,
    window.location.href
  );

  installBaseStyles();
})();


/*
  WATCHERS V2 — MOBILE COLLECTION FILTER RUNTIME
  BUILD: 2026-09-20-F14-N18

  Permanent behavior:
  - Drawer remains open during filter selection.
  - Drawer closes only through WATCHERS back, APPLY, or outside shield.
  - Shield prevents product click-through.
  - Checkbox selection collapses all open filter accordions.
  - Rapid checkbox changes are serialized.
  - Drawer selected-filter X keeps drawer open.
  - Product grid never fades during filtering.
  - One remaining product stays at normal two-column width.
  - Mobile sort swaps only collection result markup.

  N18:
  - Outside product-list summary X removes only the pressed filter.
  - Rapid outside-summary X presses keep the newest intended remaining-filter state.
  - Drawer is never automatically reopened after the customer closes it.
  - On drawer exit, the outside product-list summary is mirrored immediately.
  - Outside summary positioning is left to the collection Liquid/CSS baseline.
*/

(() => {
  'use strict';

  if (
    window.__WATCHERS_FILTER_F14__
  ) {
    return;
  }

  window.__WATCHERS_FILTER_F14__ =
    true;

  const MOBILE =
    '(max-width: 749px)';

  const SHIELD_ID =
    'WatchersFilterScreenShieldF14';

  const STYLE_ID =
    'WatchersFilterStylesF14';

  const isMobile = () =>
    window
      .matchMedia(MOBILE)
      .matches;


  let refreshQueued = false;

  let shieldStartX = 0;
  let shieldStartY = 0;
  let shieldMoved = false;

  let shieldRemovalTimer =
    null;


  /*
    DRAWER FACET SERIALIZATION
  */

  let facetBusy = false;

  let facetQueue = [];

  let facetSnapshot = null;

  let facetFinishTimer = null;

  let forceDrawerOpen = false;

  const nativeFacetInputs =
    new WeakSet();

  const queuedFacetInputs =
    new WeakSet();


  /*
    Instant sort runtime.
  */

  let sortController = null;

  let sortRequestId = 0;


  /*
    N18 — OUTSIDE PRODUCT-LIST SUMMARY X

    These variables are intentionally separate
    from the filter-drawer facet serializer.

    Drawer behavior must remain untouched.
  */

  let outsideSummaryController =
    null;

  let outsideSummaryRequestId =
    0;

  let outsideSummarySourceUrl =
    null;

  let outsideSummaryDesiredUrl =
    null;


  function installStyles() {
    if (
      document.getElementById(
        STYLE_ID
      )
    ) {
      return;
    }

    const style =
      document.createElement(
        'style'
      );

    style.id =
      STYLE_ID;

    style.textContent = `
      @media screen and (max-width: 749px) {

        details.mobile-facets__disclosure[open],
        details.mobile-facets__disclosure.menu-opening {
          position: relative !important;
          z-index: 2147483002 !important;
        }

        details.mobile-facets__disclosure[open] .mobile-facets,
        details.mobile-facets__disclosure.menu-opening .mobile-facets,
        details.mobile-facets__disclosure[open] .mobile-facets__inner,
        details.mobile-facets__disclosure.menu-opening .mobile-facets__inner {
          z-index: 2147483003 !important;
        }

        .mobile-facets__inner {
          position: relative !important;
          pointer-events: auto !important;
          touch-action: pan-y !important;
        }

        .mobile-facets__main,
        .mobile-facets__details,
        .mobile-facets__submenu,
        .mobile-facets__list,
        .mobile-facets__item,
        .mobile-facets__label {
          touch-action: pan-y !important;
        }

        .mobile-facets__header {
          position: relative !important;
        }


        .watchers-filter-back,
        .watchers-filter-back:hover,
        .watchers-filter-back:focus,
        .watchers-filter-back:focus-visible,
        .watchers-filter-back:active {
          position: absolute !important;
          left: 16px !important;
          top: 50% !important;
          width: 36px !important;
          height: 42px !important;
          margin: 0 !important;
          padding: 0 !important;
          transform: translateY(-50%) !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          border: 0 !important;
          border-radius: 0 !important;
          outline: 0 !important;
          box-shadow: none !important;
          background: transparent !important;
          color: #fff !important;
          font-family: Arial, sans-serif !important;
          font-size: 25px !important;
          font-weight: 400 !important;
          line-height: 1 !important;
          transition: none !important;
          animation: none !important;
          -webkit-appearance: none !important;
          appearance: none !important;
          -webkit-tap-highlight-color: transparent !important;
          touch-action: manipulation !important;
          user-select: none !important;
          -webkit-user-select: none !important;
          z-index: 100 !important;
        }


        .watchers-color-facet .mobile-facets__item,
        .watchers-color-facet .mobile-facets__label,
        .watchers-color-facet .facet-checkbox {
          cursor: pointer !important;
          -webkit-tap-highlight-color: transparent !important;
        }

        .watchers-color-facet .mobile-facets__label {
          display: flex !important;
          align-items: center !important;
          width: 100% !important;
        }

        .watchers-color-facet .swatch-input-wrapper {
          flex: 0 0 auto !important;
          margin-right: 7px !important;
        }

        .watchers-color-facet .swatch-input__label {
          margin: 0 !important;
        }

        .watchers-color-facet .swatch {
          width: 16px !important;
          min-width: 16px !important;
          max-width: 16px !important;
          height: 16px !important;
          min-height: 16px !important;
          max-height: 16px !important;
          flex: 0 0 16px !important;
          margin: 0 !important;
          border-radius: 50% !important;
          box-sizing: border-box !important;
        }


        #ProductGridContainer
        #product-grid
        > .grid__item:only-child {
          width: calc(50% - 5px) !important;
          max-width: calc(50% - 5px) !important;
          flex: 0 0 calc(50% - 5px) !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
        }


        /*
          Outside product-list summary positioning is intentionally NOT
          overridden here. The collection Liquid/CSS baseline owns its
          location so it does not jump when one filter remains.
        */


        /*
          Never show Dawn's white/faded loading wash.
        */

        #ProductGridContainer
        .loading-overlay,

        #ProductGridContainer
        .loading-overlay__spinner {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }

        #ProductGridContainer.loading,

        #ProductGridContainer.loading
        .collection,

        #ProductGridContainer.loading
        #product-grid,

        #ProductGridContainer
        .collection.loading,

        #ProductGridContainer
        .collection.loading
        #product-grid,

        #ProductGridContainer
        #product-grid.loading {
          opacity: 1 !important;
          visibility: visible !important;
          filter: none !important;
        }


        #${SHIELD_ID} {
          position: fixed !important;
          inset: 0 !important;
          display: block !important;
          width: 100vw !important;
          height: 100dvh !important;
          margin: 0 !important;
          padding: 0 !important;
          border: 0 !important;
          background: transparent !important;
          pointer-events: auto !important;
          touch-action: none !important;
          -webkit-tap-highlight-color: transparent !important;
          user-select: none !important;
          -webkit-user-select: none !important;
          z-index: 2147483001 !important;
        }
      }
    `;

    document.head.appendChild(
      style
    );
  }


  function cleanText(value) {
    return String(
      value || ''
    )
      .replace(
        /\s*\([\d,]+\)\s*$/,
        ''
      )
      .replace(
        /\s+/g,
        ' '
      )
      .trim();
  }


  function isGid(value) {
    return /^gid:\/\/shopify\//i
      .test(
        String(
          value || ''
        ).trim()
      );
  }


  function facetTitle(details) {
    const summary =
      details?.querySelector(
        ':scope > .mobile-facets__summary'
      );

    if (!summary) {
      return '';
    }

    const clone =
      summary.cloneNode(
        true
      );

    clone
      .querySelectorAll(
        'svg, ' +
        '.icon, ' +
        '.icon-caret, ' +
        '.mobile-facets__arrow'
      )
      .forEach(
        (node) =>
          node.remove()
      );

    return cleanText(
      clone.textContent
    ).toUpperCase();
  }


  function markColorFacets(
    root = document
  ) {
    const detailsList = [];

    if (
      root instanceof Element &&
      root.matches(
        '.mobile-facets__details'
      )
    ) {
      detailsList.push(root);
    }

    if (
      root.querySelectorAll
    ) {
      detailsList.push(
        ...root.querySelectorAll(
          '.mobile-facets__details'
        )
      );
    }

    detailsList.forEach(
      (details) => {
        details.classList.toggle(
          'watchers-color-facet',
          facetTitle(details) ===
            'COLOR'
        );
      }
    );
  }


  function getOpenDisclosure() {
    if (!isMobile()) {
      return null;
    }

    return (
      Array.from(
        document.querySelectorAll(
          'details.mobile-facets__disclosure'
        )
      ).find(
        (details) =>
          details.open ||
          details.classList.contains(
            'menu-opening'
          )
      ) ||
      null
    );
  }


  function getAnyDisclosure() {
    return (
      document.querySelector(
        'details.mobile-facets__disclosure'
      ) ||
      null
    );
  }


  function getOpenDrawer() {
    return (
      getOpenDisclosure()
        ?.querySelector(
          '.mobile-facets__inner'
        ) ||
      null
    );
  }


  function getAnyDrawer() {
    return (
      document.querySelector(
        '.mobile-facets__inner'
      ) ||
      null
    );
  }


  function releaseScrollLock() {
    const classes = [
      'overflow-hidden',
      'overflow-hidden-mobile',
      'overflow-hidden-tablet',
      'overflow-hidden-desktop'
    ];

    document.body.classList.remove(
      ...classes
    );

    document.documentElement
      .classList
      .remove(
        ...classes
      );

    if (
      document.body.style.overflow ===
      'hidden'
    ) {
      document.body.style
        .removeProperty(
          'overflow'
        );
    }

    if (
      document.documentElement
        .style.overflow ===
      'hidden'
    ) {
      document.documentElement
        .style.removeProperty(
          'overflow'
        );
    }
  }


  function getShield() {
    return document.getElementById(
      SHIELD_ID
    );
  }


  function removeShieldNow() {
    if (shieldRemovalTimer) {
      window.clearTimeout(
        shieldRemovalTimer
      );

      shieldRemovalTimer =
        null;
    }

    getShield()?.remove();
  }


  function scheduleShieldRemoval() {
    if (shieldRemovalTimer) {
      window.clearTimeout(
        shieldRemovalTimer
      );
    }

    shieldRemovalTimer =
      window.setTimeout(
        () => {
          getShield()?.remove();

          shieldRemovalTimer =
            null;
        },

        320
      );
  }


  function getDrawerPriceInputs(drawer) {
    if (!drawer) return [];

    return Array.from(
      drawer.querySelectorAll(
        'input[type="number"], input[type="text"]'
      )
    ).filter(
      (input) =>
        input.name &&
        /price/i.test(input.name)
    );
  }


  function buildDesiredFilterUrlFromDrawer(drawer) {
    const url =
      new URL(
        window.location.href
      );

    /*
      Rebuild only Shopify filter query parameters from the current
      visible drawer state. Preserve collection path + sort order.
    */

    Array.from(
      url.searchParams.keys()
    ).forEach(
      (name) => {
        if (
          name.startsWith('filter.')
        ) {
          url.searchParams.delete(
            name
          );
        }
      }
    );

    drawer
      ?.querySelectorAll(
        '.mobile-facets__submenu input[type="checkbox"]:checked'
      )
      .forEach(
        (input) => {
          if (
            input.name &&
            input.value
          ) {
            url.searchParams.append(
              input.name,
              input.value
            );
          }
        }
      );

    getDrawerPriceInputs(
      drawer
    ).forEach(
        (input) => {
          const value =
            String(
              input.value || ''
            ).trim();

          if (
            input.name &&
            value
          ) {
            url.searchParams.set(
              input.name,
              value
            );
          }
        }
      );

    url.searchParams.delete(
      'page'
    );

    url.searchParams.delete(
      'section_id'
    );

    return url;
  }


  function makeOutsideRemovalUrl(
    desiredUrl,
    name,
    value
  ) {
    const url =
      new URL(
        desiredUrl.href
      );

    deleteOneSearchValue(
      url,
      name,
      value
    );

    return url;
  }


  function mirrorDrawerSelectedFiltersToOutside(
    drawer
  ) {
    if (
      !drawer ||
      !isMobile()
    ) {
      return;
    }

    const container =
      document.getElementById(
        'ProductGridContainer'
      );

    if (!container) {
      return;
    }

    const desiredUrl =
      buildDesiredFilterUrlFromDrawer(
        drawer
      );

    const selected = [];
    const seen =
      new Set();

    drawer
      .querySelectorAll(
        '.mobile-facets__submenu input[type="checkbox"]:checked'
      )
      .forEach(
        (input) => {
          const label =
            getVisibleFilterName(
              input
            );

          if (
            !label ||
            !input.name ||
            !input.value
          ) {
            return;
          }

          const key =
            `${input.name}\u0000${input.value}`;

          if (
            seen.has(key)
          ) {
            return;
          }

          seen.add(key);

          selected.push({
            label,
            name: input.name,
            value: input.value
          });
        }
      );

    const priceInputs =
      getDrawerPriceInputs(
        drawer
      ).filter(
        (input) =>
          String(
            input.value ||
            new URL(window.location.href).searchParams.get(input.name) ||
            ''
          ).trim()
      );

    container
      .querySelector(
        ':scope > .watchers-selected-filters'
      )
      ?.remove();

    if (
      !selected.length &&
      !priceInputs.length
    ) {
      container.classList.remove(
        'watchers-has-active-filters'
      );

      outsideSummarySourceUrl =
        new URL(
          desiredUrl.href
        );

      outsideSummaryDesiredUrl =
        new URL(
          desiredUrl.href
        );

      history.replaceState(
        {
          ...(history.state || {}),
          watchersFilterMirror: true
        },
        '',
        desiredUrl.href
      );

      return;
    }

    const row =
      document.createElement(
        'div'
      );

    row.className =
      'watchers-selected-filters medium-hide large-up-hide';

    row.setAttribute(
      'data-watchers-selected-filters',
      ''
    );

    selected.forEach(
      ({
        label,
        name,
        value
      }) => {
        const chip =
          document.createElement(
            'a'
          );

        chip.className =
          'watchers-selected-filter';

        chip.href =
          makeOutsideRemovalUrl(
            desiredUrl,
            name,
            value
          ).href;

        const text =
          document.createElement(
            'span'
          );

        text.textContent =
          label;

        const remove =
          document.createElement(
            'span'
          );

        remove.setAttribute(
          'aria-hidden',
          'true'
        );

        remove.textContent =
          '×';

        chip.append(
          text,
          remove
        );

        row.appendChild(
          chip
        );
      }
    );

    if (
      priceInputs.length
    ) {
      const priceUrl =
        new URL(
          desiredUrl.href
        );

      priceInputs.forEach(
        (input) => {
          priceUrl.searchParams.delete(
            input.name
          );
        }
      );

      const chip =
        document.createElement(
          'a'
        );

      chip.className =
        'watchers-selected-filter';

      chip.href =
        priceUrl.href;

      const text =
        document.createElement(
          'span'
        );

      text.textContent =
        'PRICE';

      const remove =
        document.createElement(
          'span'
        );

      remove.setAttribute(
        'aria-hidden',
        'true'
      );

      remove.textContent =
        '×';

      chip.append(
        text,
        remove
      );

      row.appendChild(
        chip
      );
    }

    container.prepend(
      row
    );

    container.classList.add(
      'watchers-has-active-filters'
    );

    outsideSummarySourceUrl =
      new URL(
        desiredUrl.href
      );

    outsideSummaryDesiredUrl =
      new URL(
        desiredUrl.href
      );

    history.replaceState(
      {
        ...(history.state || {}),
        watchersFilterMirror: true
      },
      '',
      desiredUrl.href
    );
  }


  function closeDrawer() {
    forceDrawerOpen =
      false;

    const details =
      getOpenDisclosure();

    const drawerBeforeClose =
      details?.querySelector(
        '.mobile-facets__inner'
      ) ||
      getAnyDrawer();

    if (drawerBeforeClose) {
      mirrorDrawerSelectedFiltersToOutside(
        drawerBeforeClose
      );
    }

    if (!details) {
      scheduleShieldRemoval();
      return;
    }

    details.open =
      false;

    details.removeAttribute(
      'open'
    );

    details.classList.remove(
      'menu-opening'
    );

    const summary =
      details.querySelector(
        ':scope > summary'
      );

    summary?.setAttribute(
      'aria-expanded',
      'false'
    );

    const inner =
      details.querySelector(
        '.mobile-facets__inner'
      );

    inner?.classList.remove(
      'menu-opening'
    );

    inner?.parentElement
      ?.classList.remove(
        'menu-opening'
      );

    releaseScrollLock();

    scheduleShieldRemoval();

    document.dispatchEvent(
      new CustomEvent('watchers:filter:closed')
    );
  }


  window.__WATCHERS_CLOSE_FILTER_DRAWER__ = closeDrawer;


  function restoreDrawerOpen() {
    /*
      N18: intentionally disabled.

      The customer is the only authority that may open the filter drawer.
      AJAX completion must never reopen a drawer that the customer closed.
    */
    return;
  }


  function ensureBackButton(drawer) {
    if (!drawer) {
      return;
    }

    const header =
      drawer.querySelector(
        ':scope > .mobile-facets__header'
      );

    if (!header) {
      return;
    }

    let button =
      header.querySelector(
        '.watchers-filter-back'
      );

    if (button) {
      return;
    }

    button =
      document.createElement(
        'button'
      );

    button.type =
      'button';

    button.className =
      'watchers-filter-back';

    button.setAttribute(
      'aria-label',
      'Close filters'
    );

    button.innerHTML =
      '&#8592;';

    header.appendChild(
      button
    );
  }


  function getVisibleFilterName(
    input
  ) {
    const item =
      input?.closest(
        '.mobile-facets__item'
      );

    if (!item) {
      return '';
    }

    const candidates = [
      item.querySelector(
        '.facet-checkbox__text-label'
      ),

      item.querySelector(
        '.facet-checkbox__text'
      )
    ];

    for (
      const node
      of candidates
    ) {
      if (!node) continue;

      const name =
        cleanText(
          node.textContent
        );

      if (
        name &&
        !isGid(name)
      ) {
        return name;
      }
    }

    const label =
      input.closest(
        'label'
      ) ||
      item.querySelector(
        'label'
      );

    if (label) {
      const clone =
        label.cloneNode(
          true
        );

      clone
        .querySelectorAll(
          'input, ' +
          'svg, ' +
          '.swatch, ' +
          '.swatch-input__label, ' +
          '.visually-hidden, ' +
          '.mobile-facets__highlight, ' +
          '.icon-checkmark'
        )
        .forEach(
          (element) =>
            element.remove()
        );

      const name =
        cleanText(
          clone.textContent
        );

      if (
        name &&
        !isGid(name)
      ) {
        return name;
      }
    }

    const clone =
      item.cloneNode(
        true
      );

    clone
      .querySelectorAll(
        'input, ' +
        'svg, ' +
        '.swatch, ' +
        '.swatch-input__label, ' +
        '.visually-hidden, ' +
        '.mobile-facets__highlight, ' +
        '.icon-checkmark'
      )
      .forEach(
        (element) =>
          element.remove()
      );

    const rowName =
      cleanText(
        clone.textContent
      );

    if (
      rowName &&
      !isGid(rowName)
    ) {
      return rowName;
    }

    return '';
  }


  function ensureSelectedRow(drawer) {
    if (!drawer) {
      return null;
    }

    let row =
      drawer.querySelector(
        ':scope > .watchers-drawer-selected-filters'
      );

    if (row) {
      return row;
    }

    const header =
      drawer.querySelector(
        ':scope > .mobile-facets__header'
      );

    if (!header) {
      return null;
    }

    row =
      document.createElement(
        'div'
      );

    row.className =
      'watchers-drawer-selected-filters';

    row.setAttribute(
      'aria-label',
      'Selected filters'
    );

    header.insertAdjacentElement(
      'afterend',
      row
    );

    return row;
  }


  function syncSelectedFilters(drawer) {
    if (
      !drawer ||
      !isMobile()
    ) {
      return;
    }

    const row =
      ensureSelectedRow(
        drawer
      );

    if (!row) {
      return;
    }

    const selected = [];

    const seen =
      new Set();

    drawer
      .querySelectorAll(
        '.mobile-facets__submenu ' +
        'input[type="checkbox"]:checked'
      )
      .forEach(
        (input) => {
          const name =
            getVisibleFilterName(
              input
            );

          if (
            !name ||
            isGid(name)
          ) {
            return;
          }

          const key =
            `${input.name || ''}|` +
            `${name.toUpperCase()}`;

          if (
            seen.has(key)
          ) {
            return;
          }

          seen.add(key);

          selected.push({
            name,
            input
          });
        }
      );

    const drawerPriceInputs =
      getDrawerPriceInputs(
        drawer
      );

    const activeDrawerPriceInputs =
      drawerPriceInputs.filter(
        (input) =>
          String(
            input.value ||
            new URL(window.location.href).searchParams.get(input.name) ||
            ''
          ).trim()
      );

    if (
      activeDrawerPriceInputs.length
    ) {
      const priceDetails =
        activeDrawerPriceInputs[0]
          ?.closest(
            '.mobile-facets__details'
          );

      const priceClearLink =
        priceDetails
          ?.querySelector(
            '.mobile-facets__clear[href], .facets__reset[href]'
          ) ||
        null;

      selected.push({
        name: 'PRICE',
        input: null,
        priceClearLink
      });
    }

    row.replaceChildren();

    selected.forEach(
      ({
        name,
        input,
        priceClearLink
      }) => {
        const chip =
          document.createElement(
            'button'
          );

        chip.type =
          'button';

        chip.className =
          'watchers-drawer-selected-filter';

        chip.setAttribute(
          'aria-label',
          `Remove ${name} filter`
        );

        const text =
          document.createElement(
            'span'
          );

        text.textContent =
          name;

        const remove =
          document.createElement(
            'span'
          );

        remove.className =
          'watchers-drawer-selected-filter__remove';

        remove.setAttribute(
          'aria-hidden',
          'true'
        );

        remove.textContent =
          '×';

        chip.append(
          text,
          remove
        );

        chip.addEventListener(
          'click',

          (event) => {
            event.preventDefault();

            event.stopPropagation();

            event.stopImmediatePropagation();

            /*
              N31 — IMMEDIATE DRAWER SUMMARY SYNC

              When a filter came from a WATCHERS quicklink, the checked native
              facet input is real, but the drawer summary chip could remain
              visible until the drawer was closed.

              Remove the pressed summary chip immediately, then let the exact
              same native checkbox click continue to own the real Shopify facet
              update. This changes only the summary feedback, not the filter
              request path.
            */
            chip.remove();
            cleanDrawerSelectedRowNow(row);

            if (
              name === 'PRICE'
            ) {
              if (
                priceClearLink?.isConnected
              ) {
                priceClearLink.click();
              } else {
                const livePriceInputs =
                  getDrawerPriceInputs(
                    drawer
                  );

                livePriceInputs.forEach(
                  (priceInput) => {
                    priceInput.value = '';
                  }
                );

                const finalPriceInput =
                  livePriceInputs[
                    livePriceInputs.length - 1
                  ];

                finalPriceInput?.dispatchEvent(
                  new Event(
                    'change',
                    { bubbles: true }
                  )
                );
              }
            } else if (
              input?.isConnected &&
              input.checked
            ) {
              /*
                Native checkbox remains the source of truth.
                We only make the summary feedback immediate.
              */
              input.click();
            }

            /*
              The native facet render may replace the drawer DOM asynchronously.
              Re-sync both immediately and once more after the render frame.
            */
            if (
              drawer.isConnected
            ) {
              syncSelectedFilters(
                drawer
              );
            }

            requestAnimationFrame(
              () => {
                const liveDrawer =
                  getOpenDrawer() ||
                  document.querySelector(
                    '.mobile-facets__inner'
                  );

                if (liveDrawer) {
                  syncSelectedFilters(
                    liveDrawer
                  );
                }
              }
            );
          },

          true
        );

        row.appendChild(
          chip
        );
      }
    );

    row.hidden =
      selected.length === 0;
  }


  document.addEventListener(
    'watchers:price:state',
    () => {
      const drawer =
        getOpenDrawer() ||
        document.querySelector(
          '.mobile-facets__inner'
        );

      if (!drawer) return;

      requestAnimationFrame(
        () => {
          if (drawer.isConnected) {
            syncSelectedFilters(
              drawer
            );

            mirrorDrawerSelectedFiltersToOutside(
              drawer
            );
          }
        }
      );
    }
  );


  function cleanDrawerSelectedRowNow(row) {
    if (!row) return;

    if (
      !row.querySelector(
        '.watchers-drawer-selected-filter'
      )
    ) {
      row.replaceChildren();
      row.hidden = true;
    }
  }


  function collapseAllOpenFacets(
    drawer
  ) {
    if (!drawer) {
      return;
    }

    drawer
      .querySelectorAll(
        'details.mobile-facets__details[open], ' +
        'details.mobile-facets__details.menu-opening'
      )
      .forEach(
        (details) => {
          const summary =
            details.querySelector(
              ':scope > .mobile-facets__summary'
            );

          const submenu =
            details.querySelector(
              ':scope > .mobile-facets__submenu'
            );

          details.open =
            false;

          details.removeAttribute(
            'open'
          );

          details.classList.remove(
            'menu-opening'
          );

          summary?.setAttribute(
            'aria-expanded',
            'false'
          );

          submenu?.classList.remove(
            'menu-opening'
          );
        }
      );
  }


  function getFacetIdentity(
    input
  ) {
    const details =
      input?.closest(
        '.mobile-facets__details'
      );

    return {
      name:
        String(
          input?.name || ''
        ),

      value:
        String(
          input?.value || ''
        ),

      checked:
        Boolean(
          input?.checked
        ),

      label:
        getVisibleFilterName(
          input
        ),

      facet:
        facetTitle(
          details
        )
    };
  }


  function facetActionKey(
    action
  ) {
    return [
      action.name,
      action.value,
      action.facet,
      action.label
    ].join('|');
  }


  function queueFacetAction(
    input
  ) {
    if (!input) {
      return;
    }

    const action =
      getFacetIdentity(
        input
      );

    const key =
      facetActionKey(
        action
      );

    facetQueue =
      facetQueue.filter(
        (item) =>
          facetActionKey(
            item
          ) !== key
      );

    facetQueue.push(
      action
    );
  }


  function beginFacetRefresh() {
    if (facetBusy) {
      return;
    }

    facetBusy =
      true;

    const disclosure =
      getOpenDisclosure();

    facetSnapshot = {
      disclosure:
        getAnyDisclosure(),

      drawer:
        getAnyDrawer(),

      grid:
        document.getElementById(
          'ProductGridContainer'
        ),

      productGrid:
        document.getElementById(
          'product-grid'
        )
    };

    if (
      facetFinishTimer
    ) {
      window.clearTimeout(
        facetFinishTimer
      );
    }

    facetFinishTimer =
      window.setTimeout(
        () => {
          finishFacetRefresh();
        },

        5000
      );
  }


  function findFacetInput(
    action
  ) {
    const inputs =
      Array.from(
        document.querySelectorAll(
          '.mobile-facets__inner ' +
          'input[type="checkbox"]'
        )
      );

    let found =
      inputs.find(
        (input) =>
          String(
            input.name || ''
          ) === action.name &&
          String(
            input.value || ''
          ) === action.value
      );

    if (found) {
      return found;
    }

    found =
      inputs.find(
        (input) => {
          const details =
            input.closest(
              '.mobile-facets__details'
            );

          const sameFacet =
            !action.facet ||
            facetTitle(
              details
            ) ===
              action.facet;

          const sameLabel =
            !action.label ||
            getVisibleFilterName(
              input
            ).toUpperCase() ===
              action.label.toUpperCase();

          return (
            sameFacet &&
            sameLabel
          );
        }
      );

    return (
      found ||
      null
    );
  }


  function processNextFacetAction() {
    if (
      facetBusy ||
      !facetQueue.length
    ) {
      return;
    }

    const action =
      facetQueue.shift();

    const input =
      findFacetInput(
        action
      );

    if (!input) {
      queueMicrotask(
        processNextFacetAction
      );

      return;
    }

    if (
      Boolean(
        input.checked
      ) ===
      Boolean(
        action.checked
      )
    ) {
      queueMicrotask(
        processNextFacetAction
      );

      return;
    }

    input.click();

    queueMicrotask(
      () => {
        if (!facetBusy) {
          processNextFacetAction();
        }
      }
    );
  }


  function finishFacetRefresh() {
    if (!facetBusy) {
      return;
    }

    facetBusy =
      false;

    facetSnapshot =
      null;

    if (
      facetFinishTimer
    ) {
      window.clearTimeout(
        facetFinishTimer
      );

      facetFinishTimer =
        null;
    }

    requestAnimationFrame(
      () => {
        requestAnimationFrame(
          () => {
            processNextFacetAction();
          }
        );
      }
    );
  }


  function facetRefreshDomHasChanged(
    records
  ) {
    if (
      !facetBusy ||
      !facetSnapshot
    ) {
      return false;
    }

    const currentGrid =
      document.getElementById(
        'ProductGridContainer'
      );

    const currentProductGrid =
      document.getElementById(
        'product-grid'
      );

    const currentDrawer =
      getAnyDrawer();

    if (
      currentGrid !==
        facetSnapshot.grid ||
      currentProductGrid !==
        facetSnapshot.productGrid ||
      currentDrawer !==
        facetSnapshot.drawer ||
      !facetSnapshot.grid
        ?.isConnected ||
      !facetSnapshot.productGrid
        ?.isConnected ||
      !facetSnapshot.drawer
        ?.isConnected
    ) {
      return true;
    }

    for (
      const record
      of records
    ) {
      if (
        record.type !==
        'childList'
      ) {
        continue;
      }

      const target =
        record.target;

      if (
        target instanceof Element &&
        (
          target.id ===
            'product-grid' ||
          target.closest?.(
            '#product-grid'
          ) ||
          target.classList?.contains(
            'mobile-facets__main'
          )
        )
      ) {
        return true;
      }

      for (
        const node
        of record.addedNodes
      ) {
        if (
          !(node instanceof Element)
        ) {
          continue;
        }

        if (
          node.id ===
            'ProductGridContainer' ||
          node.id ===
            'product-grid' ||
          node.matches?.(
            '.mobile-facets__main, ' +
            '.mobile-facets__inner'
          ) ||
          node.querySelector?.(
            '#product-grid, ' +
            '.mobile-facets__main, ' +
            '.mobile-facets__inner'
          )
        ) {
          return true;
        }
      }
    }

    return false;
  }


  function ensureShield() {
    if (!isMobile()) {
      return;
    }

    if (
      shieldRemovalTimer
    ) {
      window.clearTimeout(
        shieldRemovalTimer
      );

      shieldRemovalTimer =
        null;
    }

    let shield =
      getShield();

    if (shield) {
      return shield;
    }

    shield =
      document.createElement(
        'div'
      );

    shield.id =
      SHIELD_ID;

    shield.setAttribute(
      'aria-hidden',
      'true'
    );

    shield.addEventListener(
      'pointerdown',

      (event) => {
        shieldStartX =
          event.clientX;

        shieldStartY =
          event.clientY;

        shieldMoved =
          false;

        event.preventDefault();

        event.stopPropagation();

        event.stopImmediatePropagation();
      },

      true
    );

    shield.addEventListener(
      'pointermove',

      (event) => {
        if (
          Math.abs(
            event.clientX -
            shieldStartX
          ) > 8 ||
          Math.abs(
            event.clientY -
            shieldStartY
          ) > 8
        ) {
          shieldMoved =
            true;
        }

        event.preventDefault();

        event.stopPropagation();

        event.stopImmediatePropagation();
      },

      true
    );

    shield.addEventListener(
      'pointerup',

      (event) => {
        event.preventDefault();

        event.stopPropagation();

        event.stopImmediatePropagation();

        if (!shieldMoved) {
          closeDrawer();
        }
      },

      true
    );

    shield.addEventListener(
      'pointercancel',

      (event) => {
        shieldMoved =
          true;

        event.preventDefault();

        event.stopPropagation();

        event.stopImmediatePropagation();
      },

      true
    );

    shield.addEventListener(
      'touchstart',

      (event) => {
        event.preventDefault();

        event.stopPropagation();

        event.stopImmediatePropagation();
      },

      {
        capture: true,
        passive: false
      }
    );

    shield.addEventListener(
      'mousedown',

      (event) => {
        event.preventDefault();

        event.stopPropagation();

        event.stopImmediatePropagation();
      },

      true
    );

    shield.addEventListener(
      'click',

      (event) => {
        event.preventDefault();

        event.stopPropagation();

        event.stopImmediatePropagation();
      },

      true
    );

    document.body.appendChild(
      shield
    );

    return shield;
  }


  function initializeDrawer() {
    if (!isMobile()) {
      removeShieldNow();
      return;
    }

    const drawer =
      getOpenDrawer();

    if (!drawer) {
      if (
        !shieldRemovalTimer
      ) {
        removeShieldNow();
      }

      return;
    }

    installStyles();

    ensureShield();

    markColorFacets(
      drawer
    );

    ensureBackButton(
      drawer
    );

    syncSelectedFilters(
      drawer
    );
  }


  document.addEventListener(
    'click',

    (event) => {
      if (!isMobile()) {
        return;
      }

      const back =
        event.target.closest?.(
          '.watchers-filter-back'
        );

      if (!back) {
        return;
      }

      event.preventDefault();

      event.stopPropagation();

      event.stopImmediatePropagation();

      closeDrawer();
    },

    true
  );


  document.addEventListener(
    'pointerdown',

    (event) => {
      if (!isMobile()) {
        return;
      }

      const chip =
        event.target.closest?.(
          '.watchers-drawer-selected-filter'
        );

      if (!chip) {
        return;
      }

      event.stopPropagation();

      event.stopImmediatePropagation();
    },

    true
  );


  document.addEventListener(
    'click',

    (event) => {
      if (!isMobile()) {
        return;
      }

      const item =
        event.target.closest?.(
          '.watchers-color-facet ' +
          '.mobile-facets__item'
        );

      if (!item) {
        return;
      }

      if (
        event.target.closest(
          'input[type="checkbox"], ' +
          'label, ' +
          '.swatch-input__label'
        )
      ) {
        return;
      }

      const input =
        item.querySelector(
          'input[type="checkbox"]'
        );

      if (
        !input ||
        input.disabled
      ) {
        return;
      }

      event.preventDefault();

      event.stopPropagation();

      input.click();
    },

    false
  );


  document.addEventListener(
    'input',

    (event) => {
      if (!isMobile()) {
        return;
      }

      const input =
        event.target;

      if (
        !input.matches?.(
          '.mobile-facets__inner ' +
          'input[type="checkbox"]'
        )
      ) {
        return;
      }

      if (facetBusy) {
        queuedFacetInputs.add(
          input
        );

        queueFacetAction(
          input
        );

        event.stopPropagation();

        event.stopImmediatePropagation();

        const drawer =
          input.closest(
            '.mobile-facets__inner'
          ) ||
          getOpenDrawer();

        if (drawer) {
          syncSelectedFilters(
            drawer
          );

          if (
            input.checked
          ) {
            collapseAllOpenFacets(
              drawer
            );
          }
        }

        return;
      }

      nativeFacetInputs.add(
        input
      );

      beginFacetRefresh();
    },

    true
  );


  document.addEventListener(
    'change',

    (event) => {
      if (!isMobile()) {
        return;
      }

      const input =
        event.target;

      if (
        !input.matches?.(
          '.mobile-facets__inner ' +
          'input[type="checkbox"]'
        )
      ) {
        return;
      }

      const drawer =
        input.closest(
          '.mobile-facets__inner'
        ) ||
        getOpenDrawer();


      if (
        queuedFacetInputs.has(
          input
        )
      ) {
        queuedFacetInputs.delete(
          input
        );

        event.stopPropagation();

        event.stopImmediatePropagation();

        if (drawer) {
          syncSelectedFilters(
            drawer
          );

          if (
            input.checked
          ) {
            collapseAllOpenFacets(
              drawer
            );
          }
        }

        return;
      }


      if (
        nativeFacetInputs.has(
          input
        )
      ) {
        nativeFacetInputs.delete(
          input
        );
      }


      if (drawer) {
        syncSelectedFilters(
          drawer
        );
      }


      if (
        input.checked &&
        drawer
      ) {
        collapseAllOpenFacets(
          drawer
        );
      }
    },

    true
  );


  document.addEventListener(
    'change',

    (event) => {
      if (!isMobile()) {
        return;
      }

      if (
        !event.target.matches(
          '.mobile-facets__inner ' +
          'input[type="number"]'
        )
      ) {
        return;
      }

      const drawer =
        event.target.closest(
          '.mobile-facets__inner'
        ) ||
        getOpenDrawer();

      if (drawer) {
        syncSelectedFilters(
          drawer
        );
      }
    },

    true
  );


  /*
    ============================================================
    N18 — OUTSIDE PRODUCT-LIST SUMMARY X ONLY
    ============================================================
  */


  function countSearchEntries(url) {
    const counts =
      new Map();

    for (
      const [name, value]
      of url.searchParams.entries()
    ) {
      const key =
        `${name}\u0000${value}`;

      counts.set(
        key,
        (counts.get(key) || 0) + 1
      );
    }

    return counts;
  }


  function getRemovedSearchEntries(
    sourceUrl,
    chipRemoveUrl
  ) {
    const targetCounts =
      countSearchEntries(
        chipRemoveUrl
      );

    const removed = [];

    for (
      const [name, value]
      of sourceUrl.searchParams.entries()
    ) {
      if (
        !name.startsWith('filter.')
      ) {
        continue;
      }

      const key =
        `${name}\u0000${value}`;

      const remaining =
        targetCounts.get(key) || 0;

      if (remaining > 0) {
        targetCounts.set(
          key,
          remaining - 1
        );
      } else {
        removed.push([
          name,
          value
        ]);
      }
    }

    return removed;
  }


  function deleteOneSearchValue(
    url,
    name,
    value
  ) {
    const values =
      url.searchParams.getAll(
        name
      );

    if (!values.length) {
      return;
    }

    let removed =
      false;

    url.searchParams.delete(
      name
    );

    values.forEach(
      (existingValue) => {
        if (
          !removed &&
          existingValue === value
        ) {
          removed =
            true;

          return;
        }

        url.searchParams.append(
          name,
          existingValue
        );
      }
    );
  }


  function applyOutsideSummaryRemoval(
    chipUrl
  ) {
    if (
      !outsideSummarySourceUrl ||
      !outsideSummaryDesiredUrl
    ) {
      outsideSummarySourceUrl =
        new URL(
          window.location.href
        );

      outsideSummaryDesiredUrl =
        new URL(
          window.location.href
        );
    }

    const removedEntries =
      getRemovedSearchEntries(
        outsideSummarySourceUrl,
        chipUrl
      );

    /*
      Normal Shopify filter chip:
      one or more filter query entries
      disappear from url_to_remove.

      PRICE may remove both min/max.
    */

    if (
      removedEntries.length
    ) {
      removedEntries.forEach(
        ([name, value]) => {
          deleteOneSearchValue(
            outsideSummaryDesiredUrl,
            name,
            value
          );
        }
      );
    } else {
      /*
        Safety fallback.

        If Shopify's URL shape ever prevents
        us from identifying the delta,
        use that chip URL only when no other
        rapid removal is already accumulated.
      */

      const chipFilterEntries =
        Array.from(
          chipUrl.searchParams.entries()
        ).filter(
          ([name]) =>
            name.startsWith('filter.')
        );

      const desiredNonFilter =
        new URL(
          outsideSummaryDesiredUrl.href
        );

      Array.from(
        desiredNonFilter.searchParams.keys()
      ).forEach(
        (name) => {
          if (
            name.startsWith('filter.')
          ) {
            desiredNonFilter.searchParams.delete(
              name
            );
          }
        }
      );

      chipFilterEntries.forEach(
        ([name, value]) => {
          desiredNonFilter.searchParams.append(
            name,
            value
          );
        }
      );

      outsideSummaryDesiredUrl =
        desiredNonFilter;
    }

    outsideSummaryDesiredUrl
      .searchParams
      .delete(
        'page'
      );

    outsideSummaryDesiredUrl
      .searchParams
      .delete(
        'section_id'
      );

    return new URL(
      outsideSummaryDesiredUrl.href
    );
  }


  function updateOutsideSummaryHistory(
    targetUrl
  ) {
    history.replaceState(
      {
        ...(history.state || {}),
        watchersFacetSummary: true
      },

      '',

      targetUrl.href
    );
  }


  async function runOutsideSummaryRefresh(
    targetUrl
  ) {
    if (
      outsideSummaryController
    ) {
      outsideSummaryController.abort();
    }

    outsideSummaryController =
      new AbortController();

    const requestId =
      ++outsideSummaryRequestId;

    const currentGrid =
      document.getElementById(
        'ProductGridContainer'
      );

    if (!currentGrid) {
      window.location.assign(
        targetUrl.href
      );

      return;
    }

    const sectionId =
      currentGrid
        .querySelector(
          '#product-grid'
        )
        ?.dataset.id ||

      document
        .getElementById(
          'main-collection-filters'
        )
        ?.dataset.id ||

      '';

    const fetchUrl =
      new URL(
        targetUrl.href
      );

    if (sectionId) {
      fetchUrl.searchParams.set(
        'section_id',
        sectionId
      );
    }

    document.documentElement
      .classList
      .add(
        'watchers-facet-fast-refresh'
      );

    try {
      const response =
        await fetch(
          fetchUrl.href,
          {
            method: 'GET',

            credentials:
              'same-origin',

            headers: {
              'Accept':
                'text/html',

              'X-Requested-With':
                'WATCHERS-Outside-Filter-Summary'
            },

            signal:
              outsideSummaryController.signal
          }
        );

      if (!response.ok) {
        throw new Error(
          `Outside filter request failed: ${response.status}`
        );
      }

      const html =
        await response.text();

      /*
        Another X was pressed while this
        response was travelling.

        Never let this older result render.
      */

      if (
        requestId !==
        outsideSummaryRequestId
      ) {
        return;
      }

      const nextDoc =
        new DOMParser()
          .parseFromString(
            html,
            'text/html'
          );

      const nextGrid =
        nextDoc.querySelector(
          '#ProductGridContainer'
        );

      const nextFilters =
        nextDoc.querySelector(
          '#main-collection-filters'
        );

      const nextQuicklinks =
        nextDoc.querySelector(
          '[data-watchers-smart-quicklinks]'
        );

      if (!nextGrid) {
        throw new Error(
          'Outside filter response missing #ProductGridContainer'
        );
      }

      const liveGrid =
        document.getElementById(
          'ProductGridContainer'
        );

      if (!liveGrid) {
        throw new Error(
          'Current #ProductGridContainer missing'
        );
      }

      /*
        Prepare everything before touching
        the live page.

        Current products remain visible
        until this point.
      */

      const importedGrid =
        document.importNode(
          nextGrid,
          true
        );

      let importedFilters =
        null;

      if (nextFilters) {
        importedFilters =
          document.importNode(
            nextFilters,
            true
          );
      }

      /*
        Replace the hidden/native facet source
        first, then replace products + outside
        summary together.

        This keeps future filter URLs/counts
        aligned with the newest result.
      */

      if (importedFilters) {
        const liveFilters =
          document.getElementById(
            'main-collection-filters'
          );

        if (liveFilters) {
          liveFilters.replaceWith(
            importedFilters
          );
        }
      }

      const liveQuicklinks =
        document.querySelector(
          '[data-watchers-smart-quicklinks]'
        );

      if (nextQuicklinks && liveQuicklinks) {
        liveQuicklinks.replaceWith(
          document.importNode(
            nextQuicklinks,
            true
          )
        );
      }

      liveGrid.replaceWith(
        importedGrid
      );

      if (
        typeof FacetFiltersForm !== 'undefined'
      ) {
        FacetFiltersForm.searchParamsPrev =
          targetUrl.search.slice(1);

        if (
          typeof FacetFiltersForm.storeFilterData === 'function'
        ) {
          FacetFiltersForm.storeFilterData(
            fetchUrl.href,
            html
          );
        }

        if (
          typeof FacetFiltersForm.preloadWatchersQuicklinks === 'function'
        ) {
          FacetFiltersForm.preloadWatchersQuicklinks(
            document
          );
        }
      }

      outsideSummarySourceUrl =
        new URL(
          targetUrl.href
        );

      outsideSummaryDesiredUrl =
        new URL(
          targetUrl.href
        );

      requestAnimationFrame(
        () => {
          install(
            document
          );
        }
      );
    } catch (error) {
      if (
        error &&
        error.name === 'AbortError'
      ) {
        return;
      }

      console.error(
        '[WATCHERS Outside Filter Summary]',
        error
      );

      /*
        The UI already reflects all rapid X
        removals.

        If AJAX truly fails, navigate to the
        newest combined desired URL rather
        than an older individual chip URL.
      */

      window.location.assign(
        targetUrl.href
      );
    } finally {
      if (
        requestId ===
        outsideSummaryRequestId
      ) {
        document.documentElement
          .classList
          .remove(
            'watchers-facet-fast-refresh'
          );
      }
    }
  }


  document.addEventListener(
    'click',

    (event) => {
      if (!isMobile()) {
        return;
      }

      /*
        IMPORTANT:
        ONLY chips above product list.

        Drawer chips use a BUTTON and are
        handled by the existing drawer code.
      */

      const chip =
        event.target.closest?.(
          '#ProductGridContainer ' +
          '> .watchers-selected-filters ' +
          '.watchers-selected-filter[href]'
        );

      if (!chip) {
        return;
      }

      event.preventDefault();

      event.stopPropagation();

      event.stopImmediatePropagation();

      const chipUrl =
        new URL(
          chip.href,
          window.location.href
        );

      /*
        IMMEDIATE VISUAL RESPONSE
      */

      const row =
        chip.closest(
          '[data-watchers-selected-filters]'
        );

      chip.remove();

      if (
        row &&
        !row.querySelector(
          '.watchers-selected-filter'
        )
      ) {
        row.remove();

        document
          .getElementById(
            'ProductGridContainer'
          )
          ?.classList.remove(
            'watchers-has-active-filters'
          );
      }

      /*
        Build newest desired combined state.

        Example:
          HEADWEAR + SNAPBACK + MLB

        rapid:
          X HEADWEAR
          X SNAPBACK
          X MLB

        becomes one newest URL with all three
        removed.

        Older in-flight requests are cancelled.
      */

      const targetUrl =
        applyOutsideSummaryRemoval(
          chipUrl
        );

      updateOutsideSummaryHistory(
        targetUrl
      );

      runOutsideSummaryRefresh(
        targetUrl
      );
    },

    true
  );


  function setSortUi(button) {
    const details =
      button?.closest?.(
        '[data-watchers-sort]'
      );

    const label =
      cleanText(
        button?.textContent || ''
      );

    document
      .querySelectorAll(
        '[data-watchers-sort-value]'
      )
      .forEach(
        (option) => {
          option.classList.toggle(
            'is-active',
            option === button
          );
        }
      );

    const summaryText =
      details?.querySelector(
        'summary span:first-child'
      );

    if (
      summaryText &&
      label
    ) {
      summaryText.textContent =
        `SORT: ${label}`;
    }

    details?.removeAttribute(
      'open'
    );
  }


  async function runInstantSort(
    button
  ) {
    if (
      !button ||
      !isMobile()
    ) {
      return;
    }

    const value =
      String(
        button.dataset
          .watchersSortValue ||
        ''
      ).trim();

    if (!value) {
      return;
    }

    setSortUi(
      button
    );

    const targetUrl =
      new URL(
        window.location.href
      );

    targetUrl.searchParams.set(
      'sort_by',
      value
    );

    targetUrl.searchParams.delete(
      'page'
    );

    const currentGrid =
      document.getElementById(
        'ProductGridContainer'
      );

    if (!currentGrid) {
      window.location.assign(
        targetUrl.href
      );

      return;
    }

    if (sortController) {
      sortController.abort();
    }

    sortController =
      new AbortController();

    const requestId =
      ++sortRequestId;

    const sectionId =
      currentGrid
        .querySelector(
          '#product-grid'
        )
        ?.dataset.id ||

      document
        .getElementById(
          'main-collection-filters'
        )
        ?.dataset.id ||

      '';

    const fetchUrl =
      new URL(
        targetUrl.href
      );

    if (sectionId) {
      fetchUrl.searchParams.set(
        'section_id',
        sectionId
      );
    }

    try {
      const response =
        await fetch(
          fetchUrl.href,
          {
            method: 'GET',
            credentials:
              'same-origin',

            headers: {
              'Accept':
                'text/html',

              'X-Requested-With':
                'WATCHERS-Instant-Sort'
            },

            signal:
              sortController.signal
          }
        );

      if (!response.ok) {
        throw new Error(
          `Sort request failed: ${response.status}`
        );
      }

      const html =
        await response.text();

      if (
        requestId !==
        sortRequestId
      ) {
        return;
      }

      const nextDoc =
        new DOMParser()
          .parseFromString(
            html,
            'text/html'
          );

      const nextGrid =
        nextDoc.querySelector(
          '#ProductGridContainer'
        );

      if (!nextGrid) {
        throw new Error(
          'Sorted response missing #ProductGridContainer'
        );
      }

      const liveGrid =
        document.getElementById(
          'ProductGridContainer'
        );

      if (!liveGrid) {
        throw new Error(
          'Current #ProductGridContainer missing'
        );
      }

      const importedGrid =
        document.importNode(
          nextGrid,
          true
        );

      liveGrid.replaceWith(
        importedGrid
      );

      history.replaceState(
        {
          ...(history.state || {}),
          watchersSort: true
        },

        '',

        targetUrl.href
      );

      /*
        Sort changed page URL / rendered state,
        so reset outside-summary accumulation.
      */

      outsideSummarySourceUrl =
        new URL(
          targetUrl.href
        );

      outsideSummaryDesiredUrl =
        new URL(
          targetUrl.href
        );

      requestAnimationFrame(
        () => {
          install(
            importedGrid
          );
        }
      );
    } catch (error) {
      if (
        error &&
        error.name ===
          'AbortError'
      ) {
        return;
      }

      console.error(
        '[WATCHERS Sort]',
        error
      );

      window.location.assign(
        targetUrl.href
      );
    }
  }


  document.addEventListener(
    'click',

    (event) => {
      if (!isMobile()) {
        return;
      }

      const button =
        event.target.closest?.(
          '[data-watchers-sort-value]'
        );

      if (!button) {
        return;
      }

      event.preventDefault();

      event.stopPropagation();

      event.stopImmediatePropagation();

      runInstantSort(
        button
      );
    },

    true
  );


  document.addEventListener(
    'click',

    (event) => {
      if (!isMobile()) {
        return;
      }

      const opener =
        event.target.closest?.(
          '[data-watchers-open-filter], ' +
          '.mobile-facets__open'
        );

      if (!opener) {
        return;
      }

      queueMicrotask(
        initializeDrawer
      );

      requestAnimationFrame(
        initializeDrawer
      );
    },

    false
  );


  document.addEventListener(
    'click',

    (event) => {
      if (!isMobile()) {
        return;
      }

      const apply =
        event.target.closest?.(
          '.mobile-facets__footer .button'
        );

      if (!apply) {
        return;
      }

      forceDrawerOpen =
        false;

      window.setTimeout(
        () => {
          closeDrawer();
        },

        0
      );
    },

    false
  );


  function install(
    root = document
  ) {
    if (!isMobile()) {
      removeShieldNow();
      return;
    }

    installStyles();

    markColorFacets(
      root
    );

    if (
      root.querySelectorAll
    ) {
      root
        .querySelectorAll(
          '.mobile-facets__inner'
        )
        .forEach(
          (drawer) => {
            ensureBackButton(
              drawer
            );

            syncSelectedFilters(
              drawer
            );
          }
        );
    }

    initializeDrawer();
  }


  function queueRefresh() {
    if (refreshQueued) {
      return;
    }

    refreshQueued =
      true;

    requestAnimationFrame(
      () => {
        refreshQueued =
          false;

        install(
          document
        );
      }
    );
  }


  const observer =
    new MutationObserver(
      (records) => {
        if (!isMobile()) {
          return;
        }

        if (
          facetRefreshDomHasChanged(
            records
          )
        ) {
          requestAnimationFrame(
            () => {
              requestAnimationFrame(
                finishFacetRefresh
              );
            }
          );
        }


        let relevant =
          false;

        for (
          const record
          of records
        ) {
          if (
            record.type ===
              'attributes' &&
            record.attributeName ===
              'open'
          ) {
            relevant =
              true;

            break;
          }

          if (
            record.type ===
            'childList'
          ) {
            for (
              const node
              of record.addedNodes
            ) {
              if (
                !(node instanceof Element)
              ) {
                continue;
              }

              if (
                node.matches?.(
                  '.mobile-facets__inner, ' +
                  '.mobile-facets__details, ' +
                  '#ProductGridContainer'
                ) ||

                node.querySelector?.(
                  '.mobile-facets__inner, ' +
                  '.mobile-facets__details'
                )
              ) {
                relevant =
                  true;

                break;
              }
            }
          }

          if (relevant) {
            break;
          }
        }

        if (relevant) {
          queueRefresh();
        }
      }
    );


  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'open'
      ]
    }
  );


  document.addEventListener(
    'watchers:navigation:complete',

    () => {
      outsideSummarySourceUrl =
        null;

      outsideSummaryDesiredUrl =
        null;

      requestAnimationFrame(
        () =>
          install(
            document
          )
      );
    }
  );


  document.addEventListener(
    'shopify:section:load',

    (event) => {
      requestAnimationFrame(
        () =>
          install(
            event.target
          )
      );
    }
  );


  window.addEventListener(
    'resize',

    () => {
      if (isMobile()) {
        initializeDrawer();
      } else {
        removeShieldNow();
      }
    },

    {
      passive: true
    }
  );


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',

      () => {
        outsideSummarySourceUrl =
          new URL(
            window.location.href
          );

        outsideSummaryDesiredUrl =
          new URL(
            window.location.href
          );

        install(
          document
        );
      },

      {
        once: true
      }
    );
  } else {
    outsideSummarySourceUrl =
      new URL(
        window.location.href
      );

    outsideSummaryDesiredUrl =
      new URL(
        window.location.href
      );

    install(
      document
    );
  }
})();